import { useState, useEffect, useRef } from "react";
import { THEMES } from "./theme";
import SignaturePad from "./SignaturePad";
import ICalExport from "./ICalExport";
import AutomationsView from "./AutomationsView";
import NouveautesView from "./NouveautesView";
import BrandingSettings from "./pages/settings/BrandingSettings";
import { oceanApi } from "./services/oceanApi";
import { aiApi } from "./services/aiApi";
import { odooApi } from "./services/odooApi";
import { tomtomApi } from "./services/tomtomApi";
import { kmRapportApi } from "./services/kmRapportApi";
import { kmReminderApi } from "./services/kmReminderApi";

const BASE = "https://support.gs-group.fr/api";
const h = () => ({ "Content-Type":"application/json", Authorization:"Bearer "+localStorage.getItem("nexo_token") });

const AVATAR_COLORS = ["#5b6ef8","#22d3a0","#a855f7","#ef4444","#f5a623","#14b8a6","#f97316","#06b6d4","#ec4899","#84cc16"];

const ROLES = [
  {id:"admin",      lbl:"Administrateur", ic:"👑"},
  {id:"technicien", lbl:"Technicien",     ic:"🔧"},
  {id:"dispatcher", lbl:"Dispatcher",     ic:"📋"},
  {id:"readonly",   lbl:"Lecture seule",  ic:"👁️"},
];

export const MODULES_PERMS = [
  { id:"dashboard",   lbl:"Dashboard",     ic:"🏠", desc:"Accueil et vue synthétique" },
  { id:"pipeline",    lbl:"Pipeline",      ic:"📋", desc:"Kanban SAV + interventions" },
  { id:"taches",      lbl:"Tâches",        ic:"✅", desc:"Gestionnaire de tâches perso" },
  { id:"planning",    lbl:"Planning",      ic:"📅", desc:"Calendrier d'interventions" },
  { id:"carte",       lbl:"Carte",         ic:"🗺️", desc:"Carte et géolocalisation" },
  { id:"parc-it",     lbl:"Parc IT",       ic:"🖥️", desc:"Gestion parc informatique" },
  { id:"clients",     lbl:"Clients",       ic:"👥", desc:"Fiche clients" },
  { id:"stock",       lbl:"Stock",         ic:"📦", desc:"Gestion du stock matériel" },
  { id:"vehicules",   lbl:"Flotte",        ic:"🚗", desc:"Parc automobile" },
  { id:"stats",       lbl:"Statistiques",  ic:"📊", desc:"Analyses et rapports" },
  { id:"heures",      lbl:"Heures",        ic:"⏱️", desc:"Suivi du temps techniciens" },
  { id:"settings",    lbl:"Paramètres",    ic:"⚙️", desc:"Configuration application" },
];

const SECTIONS_DISPONIBLES = [
  {id:"infos_client",ic:"👤",nom:"Informations client",desc:"Raison sociale, adresse, contact"},
  {id:"dossier",ic:"📋",nom:"Informations dossier",desc:"N° dossier, date, technicien"},
  {id:"materiels",ic:"📦",nom:"Tableau matériels",desc:"Liste équipements et quantités"},
  {id:"cameras",ic:"📷",nom:"Fiche caméras",desc:"N° CAM, localisation, IP"},
  {id:"connexion_video",ic:"🎥",nom:"Connexion vidéo",desc:"QR Code, identifiant, mdp"},
  {id:"centrale_alarme",ic:"🚨",nom:"Centrale alarme",desc:"Marque, codes, zones"},
  {id:"questions",ic:"✅",nom:"Questionnaire",desc:"NVR, internet, résumé"},
  {id:"formation",ic:"🎓",nom:"Formation client",desc:"Formation, satisfaction"},
  {id:"photos",ic:"📸",nom:"Photos",desc:"Photos obligatoires"},
  {id:"signatures",ic:"✍️",nom:"Signatures",desc:"Signature tech + client"},
];

export default function SettingsView({ T, currentUser, toggleTheme, isDark, onInvite }) {
  const [tab, setTab]           = useState("themes");
  const [saved, setSaved]       = useState("");
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const fileRef = useRef(null);

  // Profil
  const [profil, setProfil] = useState({
    prenom: currentUser?.prenom||"",
    nom: currentUser?.nom||"",
    telephone: currentUser?.telephone||"",
    theme_id: currentUser?.theme_id||"dark_indigo",
    avatar_color: currentUser?.avatar_color||"#5b6ef8",
    avatar_url: currentUser?.avatar_url||localStorage.getItem('nexo_avatar_url')||"",
  });
  const [pwd, setPwd] = useState({nouveau:"",confirm:""});
  const [pwdErr, setPwdErr] = useState("");

  // Équipe
  const [users, setUsers]           = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch]   = useState("");
  const [savingPerms, setSavingPerms] = useState(false);
  const [userModules, setUserModules] = useState({});
  const [showAddUser, setShowAddUser] = useState(false);
  const [addForm, setAddForm]         = useState({prenom:"",nom:"",email:"",role:"technicien",telephone:""});
  const [addErr, setAddErr]           = useState("");
  const [savingAdd, setSavingAdd]     = useState(false);

  // Sauvegarde
  const [backupLoading, setBackupLoading] = useState(false);

  // Documentation
  const [docLoading, setDocLoading] = useState(false);

  // API & Intégrations
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  // Intelligence Artificielle — clés OpenAI / Anthropic
  const [aiCfg, setAiCfg]           = useState(null);
  const [loadingAi, setLoadingAi]   = useState(false);
  const [savingAi, setSavingAi]     = useState(false);
  const [testingAi, setTestingAi]   = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null);
  const [aiForm, setAiForm] = useState({ openaiKey:"", anthropicKey:"" });

  // GPS Flotte — API Océan
  const [oceanCfg, setOceanCfg]           = useState(null);
  const [loadingOcean, setLoadingOcean]   = useState(false);
  const [savingOcean, setSavingOcean]     = useState(false);
  const [testingOcean, setTestingOcean]   = useState(false);
  const [oceanTestResult, setOceanTestResult] = useState(null);
  const [oceanForm, setOceanForm] = useState({ enabled:false, baseUrl:"", loginPath:"/auth/authenticate2", positionsPath:"/mobility/v1/vehiclePositions", username:"", password:"", privacyEnabled:false, privacyStart:"17:30", privacyEnd:"07:00" });
  const [kmSyncStatus, setKmSyncStatus] = useState(null);
  const [runningKmSync, setRunningKmSync] = useState(false);
  const [kmSyncResult, setKmSyncResult] = useState(null);
  const [kmRapportCfg, setKmRapportCfg] = useState(null);
  const [kmRapportForm, setKmRapportForm] = useState({ enabled:false, destinataires:[], jourSemaine:1, heure:"08:00", prixCarburant:1.80 });
  const [newKmDest, setNewKmDest] = useState("");
  const [savingKmRapport, setSavingKmRapport] = useState(false);
  const [sendingKmRapportNow, setSendingKmRapportNow] = useState(false);
  const [kmRapportResult, setKmRapportResult] = useState(null);

  // Rappel de saisie kilométrique (véhicules hors suivi Océan)
  const [kmReminderCfg, setKmReminderCfg] = useState(null);
  const [kmReminderForm, setKmReminderForm] = useState({ enabled:true, jourSemaine:1, heure:"07:00" });
  const [savingKmReminder, setSavingKmReminder] = useState(false);
  const [kmReminderVehicules, setKmReminderVehicules] = useState([]);
  const [loadingKmReminderVeh, setLoadingKmReminderVeh] = useState(false);
  const [sendingKmReminderId, setSendingKmReminderId] = useState(null);
  const [sendingKmReminderAll, setSendingKmReminderAll] = useState(false);
  const [kmReminderResult, setKmReminderResult] = useState(null);

  // Odoo — Facturation
  const [odooCfg, setOdooCfg]           = useState(null);
  const [loadingOdoo, setLoadingOdoo]   = useState(false);
  const [savingOdoo, setSavingOdoo]     = useState(false);
  const [testingOdoo, setTestingOdoo]   = useState(false);
  const [odooTestResult, setOdooTestResult] = useState(null);
  const [odooForm, setOdooForm] = useState({ enabled:false, baseUrl:"", database:"", username:"", apiKey:"", signTemplateName:"" });
  const [syncingFleet, setSyncingFleet] = useState(false);
  const [fleetSyncResult, setFleetSyncResult] = useState(null);

  // Trafic — TomTom
  const [tomtomCfg, setTomtomCfg]           = useState(null);
  const [loadingTomtom, setLoadingTomtom]   = useState(false);
  const [savingTomtom, setSavingTomtom]     = useState(false);
  const [testingTomtom, setTestingTomtom]   = useState(false);
  const [tomtomTestResult, setTomtomTestResult] = useState(null);
  const [tomtomForm, setTomtomForm] = useState({ enabled:false, apiKey:"" });
  const [tomtomZones, setTomtomZones] = useState([]);
  const [newZoneName, setNewZoneName] = useState("");
  const [addingZone, setAddingZone] = useState(false);
  const [zoneError, setZoneError] = useState("");
  const [depotAdresse, setDepotAdresse] = useState("");
  const [savingDepot, setSavingDepot] = useState(false);
  const [depotError, setDepotError] = useState("");

  // Modèles PV
  const [selectedModele, setSelectedModele] = useState("pack_sentinelle");
  const [modeles, setModeles] = useState(()=>{
    try{return JSON.parse(localStorage.getItem("gs_modeles"))||{
      pack_sentinelle:{nom:"Pack Sentinelle",icon:"📷",color:"#5b6ef8",sections:["infos_client","dossier","materiels","cameras","questions","signatures"]},
      pack_flash:{nom:"Pack Flash Control",icon:"🚨",color:"#ef4444",sections:["infos_client","dossier","centrale_alarme","questions","signatures"]},
      maintenance:{nom:"Maintenance",icon:"🔧",color:"#22d3a0",sections:["infos_client","dossier","questions","signatures"]},
      depannage:{nom:"Dépannage SAV",icon:"🛠️",color:"#f5a623",sections:["infos_client","dossier","questions","signatures"]},
    };}catch{return {};}
  });

  useEffect(()=>{
    if(tab==="equipe") loadUsers();
    if(tab==="api"){ loadApiKeys(); loadOceanConfig(); loadKmSyncStatus(); loadAiConfig(); loadOdooConfig(); loadTomtomConfig(); loadKmRapportConfig(); loadKmReminder(); }
  },[tab]);

  const loadAiConfig = async () => {
    setLoadingAi(true);
    try {
      const cfg = await aiApi.getConfig();
      setAiCfg(cfg);
      setAiForm({ openaiKey:"", anthropicKey:"" });
    } catch {}
    setLoadingAi(false);
    setAiTestResult(null);
  };

  const saveAiConfig = async () => {
    setSavingAi(true); setAiTestResult(null);
    try {
      const saved = await aiApi.saveConfig(aiForm);
      setAiCfg(saved);
      setAiForm({ openaiKey:"", anthropicKey:"" });
    } catch(e) { setAiTestResult({ error:e.message }); }
    setSavingAi(false);
  };

  const testAiConfig = async () => {
    setTestingAi(true); setAiTestResult(null);
    try {
      const result = await aiApi.testConfig(aiForm);
      setAiTestResult(result);
    } catch(e) { setAiTestResult({ error:e.message }); }
    setTestingAi(false);
  };

  const loadKmSyncStatus = async () => {
    try { setKmSyncStatus(await oceanApi.getKmSyncStatus()); } catch { /* silencieux — panneau admin uniquement */ }
  };

  const runKmSyncNow = async () => {
    setRunningKmSync(true); setKmSyncResult(null);
    try {
      const result = await oceanApi.runKmSync();
      setKmSyncResult({ ok:true, ...result });
      loadKmSyncStatus();
    } catch(e) { setKmSyncResult({ ok:false, error:e.message }); }
    setRunningKmSync(false);
  };

  const loadKmRapportConfig = async () => {
    try {
      const cfg = await kmRapportApi.getConfig();
      setKmRapportCfg(cfg);
      setKmRapportForm({ enabled:cfg.enabled, destinataires:cfg.destinataires||[], jourSemaine:cfg.jourSemaine, heure:cfg.heure, prixCarburant:cfg.prixCarburant ?? 1.80 });
    } catch { /* silencieux — panneau admin uniquement */ }
    setKmRapportResult(null);
  };

  const addKmDest = () => {
    const email = newKmDest.trim();
    if(!email || kmRapportForm.destinataires.includes(email)) return;
    setKmRapportForm(f=>({...f, destinataires:[...f.destinataires, email]}));
    setNewKmDest("");
  };
  const removeKmDest = (email) => setKmRapportForm(f=>({...f, destinataires:f.destinataires.filter(d=>d!==email)}));

  const saveKmRapportConfig = async () => {
    setSavingKmRapport(true); setKmRapportResult(null);
    try {
      const saved = await kmRapportApi.saveConfig(kmRapportForm);
      setKmRapportCfg(saved);
    } catch(e) { setKmRapportResult({ ok:false, error:e.message }); }
    setSavingKmRapport(false);
  };

  const sendKmRapportNow = async () => {
    setSendingKmRapportNow(true); setKmRapportResult(null);
    try {
      const res = await kmRapportApi.sendNow(kmRapportForm.destinataires);
      setKmRapportResult({ ok:true, ...res });
    } catch(e) { setKmRapportResult({ ok:false, error:e.message }); }
    setSendingKmRapportNow(false);
  };

  const loadKmReminder = async () => {
    try {
      const cfg = await kmReminderApi.getConfig();
      setKmReminderCfg(cfg);
      setKmReminderForm({ enabled:cfg.enabled, jourSemaine:cfg.jourSemaine, heure:cfg.heure });
    } catch { /* silencieux — panneau admin uniquement */ }
    setLoadingKmReminderVeh(true);
    try { setKmReminderVehicules(await kmReminderApi.getVehicules()); } catch {}
    setLoadingKmReminderVeh(false);
  };

  const saveKmReminderConfig = async () => {
    setSavingKmReminder(true); setKmReminderResult(null);
    try {
      const saved = await kmReminderApi.saveConfig(kmReminderForm);
      setKmReminderCfg(saved);
    } catch(e) { setKmReminderResult({ ok:false, error:e.message }); }
    setSavingKmReminder(false);
  };

  const sendKmReminderOne = async (veh) => {
    setSendingKmReminderId(veh.id); setKmReminderResult(null);
    try {
      const res = await kmReminderApi.sendOne(veh.id);
      setKmReminderResult({ ok:res.emailOk, error:res.emailOk?null:(res.emailError||"Email non envoyé"), destEmail:res.destEmail, immatriculation:veh.immatriculation });
    } catch(e) { setKmReminderResult({ ok:false, error:e.message, immatriculation:veh.immatriculation }); }
    setSendingKmReminderId(null);
  };

  const sendKmReminderAll = async () => {
    setSendingKmReminderAll(true); setKmReminderResult(null);
    try {
      const res = await kmReminderApi.sendAll();
      setKmReminderResult({ ok:true, bulk:true, ...res });
      loadKmReminder();
    } catch(e) { setKmReminderResult({ ok:false, error:e.message }); }
    setSendingKmReminderAll(false);
  };

  const loadOceanConfig = async () => {
    setLoadingOcean(true);
    try {
      const cfg = await oceanApi.getConfig();
      setOceanCfg(cfg);
      setOceanForm({
        enabled: cfg.enabled, baseUrl: cfg.baseUrl||"", loginPath: cfg.loginPath||"/auth/authenticate2",
        positionsPath: cfg.positionsPath||"/mobility/v1/vehiclePositions", username: cfg.username||"", password:"",
        privacyEnabled: !!cfg.privacyEnabled, privacyStart: cfg.privacyStart||"17:30", privacyEnd: cfg.privacyEnd||"07:00",
      });
    } catch {}
    setLoadingOcean(false);
    setOceanTestResult(null);
  };

  const saveOceanConfig = async () => {
    setSavingOcean(true); setOceanTestResult(null);
    try {
      const saved = await oceanApi.saveConfig(oceanForm);
      setOceanCfg(saved);
      setOceanForm(f=>({...f, password:""}));
    } catch(e) { setOceanTestResult({ ok:false, error:e.message }); }
    setSavingOcean(false);
  };

  const testOceanConfig = async () => {
    setTestingOcean(true); setOceanTestResult(null);
    try {
      const result = await oceanApi.testConfig(oceanForm);
      setOceanTestResult(result);
    } catch(e) { setOceanTestResult({ ok:false, error:e.message }); }
    setTestingOcean(false);
  };

  const loadOdooConfig = async () => {
    setLoadingOdoo(true);
    try {
      const cfg = await odooApi.getConfig();
      setOdooCfg(cfg);
      setOdooForm({ enabled:cfg.enabled, baseUrl:cfg.baseUrl, database:cfg.database, username:cfg.username, apiKey:"", signTemplateName:cfg.signTemplateName||"" });
    } catch { /* silencieux — panneau admin uniquement */ }
    setLoadingOdoo(false);
    setOdooTestResult(null);
  };

  const saveOdooConfig = async () => {
    setSavingOdoo(true); setOdooTestResult(null);
    try {
      const saved = await odooApi.saveConfig(odooForm);
      setOdooCfg(saved);
      setOdooForm(f=>({...f, apiKey:""}));
    } catch(e) { setOdooTestResult({ ok:false, error:e.message }); }
    setSavingOdoo(false);
  };

  const testOdooConfig = async () => {
    setTestingOdoo(true); setOdooTestResult(null);
    try {
      const result = await odooApi.testConfig(odooForm);
      setOdooTestResult(result);
    } catch(e) { setOdooTestResult({ ok:false, error:e.message }); }
    setTestingOdoo(false);
  };

  const loadTomtomConfig = async () => {
    setLoadingTomtom(true);
    try {
      const cfg = await tomtomApi.getConfig();
      setTomtomCfg(cfg);
      setTomtomForm({ enabled:cfg.enabled, apiKey:"" });
      setDepotAdresse(cfg.depotAdresse||"");
      setTomtomZones(await tomtomApi.getZones());
    } catch { /* silencieux — panneau admin uniquement */ }
    setLoadingTomtom(false);
    setTomtomTestResult(null);
  };

  const saveTomtomConfig = async () => {
    setSavingTomtom(true); setTomtomTestResult(null);
    try {
      const saved = await tomtomApi.saveConfig(tomtomForm);
      setTomtomCfg(saved);
      setTomtomForm(f=>({...f, apiKey:""}));
    } catch(e) { setTomtomTestResult({ ok:false, error:e.message }); }
    setSavingTomtom(false);
  };

  const testTomtomConfig = async () => {
    setTestingTomtom(true); setTomtomTestResult(null);
    try {
      const result = await tomtomApi.testConfig(tomtomForm);
      setTomtomTestResult(result);
    } catch(e) { setTomtomTestResult({ ok:false, error:e.message }); }
    setTestingTomtom(false);
  };

  const addTomtomZone = async () => {
    if(!newZoneName.trim()) return;
    setAddingZone(true); setZoneError("");
    try {
      await tomtomApi.addZone(newZoneName.trim());
      setNewZoneName("");
      setTomtomZones(await tomtomApi.getZones());
    } catch(e) { setZoneError(e.message); }
    setAddingZone(false);
  };

  const removeTomtomZone = async (id) => {
    await tomtomApi.deleteZone(id);
    setTomtomZones(z=>z.filter(x=>x.id!==id));
  };

  const saveDepot = async () => {
    if(!depotAdresse.trim()) return;
    setSavingDepot(true); setDepotError("");
    try {
      const saved = await tomtomApi.saveDepot(depotAdresse.trim());
      setTomtomCfg(saved);
    } catch(e) { setDepotError(e.message); }
    setSavingDepot(false);
  };

  const syncFleet = async () => {
    setSyncingFleet(true); setFleetSyncResult(null);
    try {
      const result = await odooApi.syncFleet();
      setFleetSyncResult({ ok:true, ...result });
    } catch(e) { setFleetSyncResult({ ok:false, error:e.message }); }
    setSyncingFleet(false);
  };

  const loadApiKeys = async () => {
    setLoadingKeys(true);
    const res = await fetch(`${BASE}/api-keys`,{headers:h()}).then(r=>r.json()).catch(()=>[]);
    setApiKeys(Array.isArray(res)?res:[]);
    setLoadingKeys(false);
  };

  const createApiKey = async () => {
    if(!newKeyLabel.trim()) return;
    setCreatingKey(true);
    const res = await fetch(`${BASE}/api-keys`,{method:"POST",headers:h(),body:JSON.stringify({label:newKeyLabel.trim()})}).then(r=>r.json()).catch(()=>null);
    setCreatingKey(false);
    if(res?.id){
      setNewKeyLabel("");
      setApiKeys(k=>[res,...k]);
      setRevealedKey(res.id);
    }
  };

  const revokeApiKey = async (id) => {
    if(!window.confirm("Révoquer cette clé ? Toute intégration qui l'utilise cessera de fonctionner.")) return;
    await fetch(`${BASE}/api-keys/${id}/revoke`,{method:"PATCH",headers:h()});
    setApiKeys(k=>k.map(x=>x.id===id?{...x,actif:false}:x));
  };

  const deleteApiKey = async (id) => {
    if(!window.confirm("Supprimer définitivement cette clé ?")) return;
    await fetch(`${BASE}/api-keys/${id}`,{method:"DELETE",headers:h()});
    setApiKeys(k=>k.filter(x=>x.id!==id));
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(()=>setCopiedKey(null),1500);
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    const res = await fetch(`${BASE}/users`,{headers:h()}).then(r=>r.json()).catch(()=>[]);
    setUsers(Array.isArray(res)?res:[]);
    setLoadingUsers(false);
  };

  const saveOk = (msg) => { setSaved(msg); setTimeout(()=>setSaved(""),2500); };

  // Sauvegarder profil
  const saveProfil = async () => {
    await fetch(`${BASE}/users/${currentUser.id}/preferences`,{
      method:"PATCH",headers:h(),
      body:JSON.stringify({theme_id:profil.theme_id,avatar_color:profil.avatar_color,avatar_url:profil.avatar_url||null})
    });
    const u = {...currentUser,...profil};
    localStorage.setItem("nexo_user",JSON.stringify(u));
    if(profil.avatar_url) localStorage.setItem('nexo_avatar_url', profil.avatar_url);
    if(profil.theme_id) localStorage.setItem('nexo_theme_id', profil.theme_id);
    if(profil.avatar_color) localStorage.setItem('nexo_avatar_color', profil.avatar_color);
    saveOk("✅ Profil sauvegardé !");
    setTimeout(()=>window.location.reload(),1000);
  };

  // Changer mot de passe
  const changePwd = async () => {
    if(pwd.nouveau!==pwd.confirm){setPwdErr("Mots de passe différents");return;}
    if(pwd.nouveau.length<6){setPwdErr("Minimum 6 caractères");return;}
    const res = await fetch(`${BASE}/users/${currentUser.id}/password`,{
      method:"PATCH",headers:h(),body:JSON.stringify({password:pwd.nouveau})
    }).then(r=>r.json());
    if(res.error){setPwdErr(res.error);return;}
    setPwd({nouveau:"",confirm:""}); setPwdErr("");
    saveOk("✅ Mot de passe modifié !");
  };

  // Photo de profil
  const handlePhoto = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => setProfil(p=>({...p,avatar_url:e.target.result}));
    reader.readAsDataURL(file);
  };

  // Sélectionner un utilisateur pour éditer ses permissions
  const openUser = (u) => {
    setSelectedUser(u);
    const mods = u.preferences?.modules || {};
    setUserModules(mods);
  };

  // Sauvegarder permissions modules
  const saveModules = async () => {
    if(!selectedUser) return;
    setSavingPerms(true);
    const res = await fetch(`${BASE}/users/${selectedUser.id}/modules`,{
      method:"PATCH",headers:h(),body:JSON.stringify({modules:userModules})
    }).then(r=>r.json()).catch(()=>null);
    if(res){
      setUsers(p=>p.map(u=>u.id===selectedUser.id?{...u,preferences:{...(u.preferences||{}),modules:userModules}}:u));
      setSelectedUser(prev=>({...prev,preferences:{...(prev.preferences||{}),modules:userModules}}));
      saveOk("✅ Accès mis à jour !");
    }
    setSavingPerms(false);
  };

  // Changer rôle utilisateur
  const changeRole = async (userId, role) => {
    await fetch(`${BASE}/users/${userId}`,{method:"PATCH",headers:h(),body:JSON.stringify({role})});
    setUsers(p=>p.map(u=>u.id===userId?{...u,role}:u));
    if(selectedUser?.id===userId) setSelectedUser(prev=>({...prev,role}));
    saveOk("✅ Rôle mis à jour !");
  };

  // Désactiver / réactiver utilisateur
  const toggleUserActif = async (u) => {
    const isActif = u.statut==='actif';
    await fetch(`${BASE}/users/${u.id}`,{method:"PATCH",headers:h(),body:JSON.stringify({statut:isActif?'inactif':'actif'})});
    setUsers(p=>p.map(x=>x.id===u.id?{...x,statut:isActif?'inactif':'actif'}:x));
    if(selectedUser?.id===u.id) setSelectedUser(prev=>({...prev,statut:isActif?'inactif':'actif'}));
    saveOk(isActif?"✅ Utilisateur désactivé":"✅ Utilisateur réactivé");
  };

  // Envoyer un lien de réinitialisation de mot de passe
  const sendResetLink = async (u) => {
    const res = await fetch(`${BASE}/users/${u.id}/send-reset`, { method:"POST", headers:h() }).then(r=>r.json()).catch(()=>({error:"Erreur réseau"}));
    if (res.error) { alert("Erreur : "+res.error); return; }
    if (res.email_sent) saveOk("✅ Lien envoyé par email !");
    else saveOk(`✅ Lien généré (email non configuré)`);
  };

  // Désactiver la 2FA d'un utilisateur
  const disableMFA = async (u) => {
    if (!window.confirm(`Désactiver la 2FA pour ${u.prenom} ${u.nom} ?`)) return;
    await fetch(`${BASE}/users/${u.id}/mfa`, { method:"PATCH", headers:h(), body:JSON.stringify({enabled:false}) });
    setUsers(p=>p.map(x=>x.id===u.id?{...x,mfa_enabled:false}:x));
    if (selectedUser?.id===u.id) setSelectedUser(prev=>({...prev,mfa_enabled:false}));
    saveOk("✅ 2FA désactivée");
  };

  // Inviter (créer) un nouvel utilisateur
  const inviteUser = async () => {
    if(!addForm.email||!addForm.prenom||!addForm.nom){setAddErr("Champs obligatoires manquants");return;}
    setSavingAdd(true); setAddErr("");
    const res = await fetch(`${BASE}/users/invite`,{method:"POST",headers:h(),body:JSON.stringify(addForm)}).then(r=>r.json()).catch(()=>({error:"Erreur réseau"}));
    setSavingAdd(false);
    if(res.error){setAddErr(res.error);return;}
    saveOk("✅ Invitation envoyée !");
    setShowAddUser(false);
    setAddForm({prenom:"",nom:"",email:"",role:"technicien",telephone:""});
    loadUsers();
  };

  const downloadDocumentation = async () => {
    setDocLoading(true);
    try {
      const res = await fetch(`${BASE}/documentation/pdf`, { headers: { Authorization: "Bearer " + localStorage.getItem("nexo_token") } });
      if (!res.ok) { const err = await res.json().catch(()=>({error:"Erreur serveur"})); alert("Erreur : " + (err.error||res.statusText)); setDocLoading(false); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Documentation_CRM_GS-GROUP_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      saveOk("✅ Documentation téléchargée !");
    } catch(e) { alert("Erreur réseau : " + e.message); }
    setDocLoading(false);
  };

  const downloadArchitecture = async () => {
    try {
      const res = await fetch(`${BASE}/documentation/architecture`, { headers: { Authorization: "Bearer " + localStorage.getItem("nexo_token") } });
      if (!res.ok) { const err = await res.json().catch(()=>({error:"Erreur serveur"})); alert("Erreur : " + (err.error||res.statusText)); return; }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = "Architecture_CRM_GS-GROUP.md";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch(e) { alert("Erreur réseau : " + e.message); }
  };

  const downloadBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await fetch(`${BASE}/backup`, { headers: { Authorization: "Bearer " + localStorage.getItem("nexo_token") } });
      if (!res.ok) { const err = await res.json().catch(()=>({error:"Erreur serveur"})); alert("Erreur : " + (err.error||res.statusText)); setBackupLoading(false); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sauvegarde-crm-${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      saveOk("✅ Sauvegarde téléchargée !");
    } catch(e) { alert("Erreur réseau : " + e.message); }
    setBackupLoading(false);
  };

  // Modèles PV
  const m = modeles[selectedModele]||{sections:[]};
  const addSection = (id) => { if(m.sections.includes(id))return; setModeles(p=>({...p,[selectedModele]:{...p[selectedModele],sections:[...p[selectedModele].sections,id]}})); };
  const removeSection = (id) => setModeles(p=>({...p,[selectedModele]:{...p[selectedModele],sections:p[selectedModele].sections.filter(s=>s!==id)}}));
  const moveSection = (from,to) => {
    const s=[...m.sections]; const [it]=s.splice(from,1); s.splice(to,0,it);
    setModeles(p=>({...p,[selectedModele]:{...p[selectedModele],sections:s}}));
  };
  const saveModeles = () => { localStorage.setItem("gs_modeles",JSON.stringify(modeles)); saveOk("✅ Modèles sauvegardés !"); };

  const initials = `${profil.prenom?.[0]||""}${profil.nom?.[0]||""}`.toUpperCase();

  const isAdmin = currentUser?.role === 'admin';

  const TABS = [
    { id:"nouveautes",  ic:"🆕", lbl:"Nouveautés",         group:"Mon compte" },
    { id:"documentation", ic:"📄", lbl:"Documentation",   group:"Mon compte" },
    { id:"themes",      ic:"🎨", lbl:"Profil & Thème",    group:"Mon compte" },
    { id:"alertes",     ic:"🔔", lbl:"Notifications",      group:"Mon compte" },
    { id:"automations", ic:"⚡", lbl:"Automatisations",    group:"Configuration" },
    { id:"modeles",     ic:"📋", lbl:"Modèles PV",         group:"Configuration" },
    { id:"agenda",      ic:"📅", lbl:"Agenda iCal",        group:"Configuration" },
    ...(isAdmin ? [{ id:"branding",    ic:"🖼️", lbl:"Identité visuelle", group:"Administration" }] : []),
    ...(isAdmin ? [{ id:"equipe",      ic:"👥", lbl:"Équipe & Accès", group:"Administration" }] : []),
    ...(isAdmin ? [{ id:"api",         ic:"🔌", lbl:"API & Intégrations", group:"Administration" }] : []),
    ...(isAdmin ? [{ id:"sauvegarde",  ic:"💾", lbl:"Sauvegarde",      group:"Administration" }] : []),
  ];

  const inp = (key, ph="", type="text", obj=profil, setter=setProfil) => (
    <input value={obj[key]||""} onChange={e=>setter(p=>({...p,[key]:e.target.value}))} placeholder={ph} type={type}
      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:14,outline:"none",width:"100%"}}/>
  );

  const groups = [...new Set(TABS.map(t => t.group))];

  const filteredUsers = users.filter(u=>{
    if(!userSearch) return true;
    const q=userSearch.toLowerCase();
    return (u.prenom+" "+u.nom+" "+u.email).toLowerCase().includes(q);
  });

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:24,fontWeight:900,color:T.text,letterSpacing:"-0.5px"}}>⚙️ Paramètres</div>
          <div style={{fontSize:13,color:T.muted,marginTop:4}}>Profil · Automatisations · Modèles PV{isAdmin?" · Équipe & Accès":""}</div>
        </div>
        {saved&&<div style={{padding:"10px 18px",borderRadius:11,background:T.success,color:"#fff",fontSize:13,fontWeight:700,animation:"fadeIn 0.2s"}}>{saved}</div>}
      </div>

      {/* Layout: sidebar nav + content */}
      <div style={{display:"flex",gap:18,alignItems:"start"}}>

        {/* Sidebar nav */}
        <div style={{width:200,flexShrink:0,display:"flex",flexDirection:"column",gap:4,position:"sticky",top:16}}>
          {groups.map(g => (
            <div key={g}>
              <div style={{fontSize:9,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:"1.2px",padding:"8px 12px 4px"}}>{g}</div>
              {TABS.filter(t=>t.group===g).map(t => (
                <button key={t.id} onClick={()=>setTab(t.id)}
                  style={{display:"flex",alignItems:"center",gap:9,width:"100%",padding:"9px 12px",borderRadius:9,border:"none",background:tab===t.id?T.accentDim:"transparent",color:tab===t.id?T.accent:T.muted,fontSize:13,fontWeight:tab===t.id?700:500,cursor:"pointer",textAlign:"left",transition:"all 0.12s",position:"relative"}}
                  onMouseEnter={e=>{if(tab!==t.id){e.currentTarget.style.background=T.bg;e.currentTarget.style.color=T.text;}}}
                  onMouseLeave={e=>{if(tab!==t.id){e.currentTarget.style.background="transparent";e.currentTarget.style.color=T.muted;}}}>
                  {tab===t.id&&<div style={{position:"absolute",left:0,top:"20%",bottom:"20%",width:3,borderRadius:2,background:T.accent}}/>}
                  <span style={{fontSize:16}}>{t.ic}</span>
                  <span style={{flex:1}}>{t.lbl}</span>
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Content area */}
        <div style={{flex:1,minWidth:0}}>

      {/* ═══ THÈMES + PROFIL ═══ */}
      {tab==="nouveautes"&&<NouveautesView T={T}/>}

      {/* ═══ DOCUMENTATION ═══ */}
      {tab==="documentation"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:15,padding:24}}>
            <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:6}}>📄 Documentation du CRM</div>
            <div style={{fontSize:13,color:T.muted,marginBottom:20}}>Génère un PDF présentant l'ensemble des fonctionnalités actuelles du logiciel ainsi que l'historique complet des évolutions livrées.</div>

            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10,marginBottom:22}}>
              {[
                {ic:"🧩",lbl:"Fonctionnalités par module"},
                {ic:"🕘",lbl:"Historique des évolutions"},
                {ic:"🔐",lbl:"Rôles et accès"},
                {ic:"⚙️",lbl:"Stack technique"},
              ].map(item=>(
                <div key={item.lbl} style={{display:"flex",alignItems:"center",gap:9,padding:"10px 13px",background:T.bg,border:`1px solid ${T.border}`,borderRadius:10}}>
                  <span style={{fontSize:18}}>{item.ic}</span>
                  <span style={{fontSize:12,fontWeight:600,color:T.text}}>{item.lbl}</span>
                </div>
              ))}
            </div>

            <button
              onClick={downloadDocumentation}
              disabled={docLoading}
              style={{width:"100%",padding:"15px",borderRadius:12,background:docLoading?"#6b7280":`linear-gradient(135deg,${T.accent},${T.purple})`,border:"none",color:"#fff",fontSize:15,fontWeight:800,cursor:docLoading?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,transition:"all 0.2s"}}>
              {docLoading
                ? <><span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⏳</span> Génération en cours…</>
                : <>📄 Télécharger la documentation PDF</>}
            </button>
            {docLoading&&<div style={{fontSize:12,color:T.muted,textAlign:"center",marginTop:8}}>La génération peut prendre quelques secondes.</div>}
            {currentUser?.role==="admin"&&(
              <button onClick={downloadArchitecture}
                style={{width:"100%",marginTop:12,padding:"13px",borderRadius:12,background:T.bg,border:`1px solid ${T.border}`,color:T.text,fontSize:14,fontWeight:700,cursor:"pointer"}}>
                🗂️ Télécharger l'architecture du code (Markdown)
              </button>
            )}
          </div>
        </div>
      )}

      {/* ═══ IDENTITÉ VISUELLE ═══ */}
      {tab==="branding"&&<BrandingSettings T={T}/>}

      {tab==="themes"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {/* Mon compte */}
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:15,padding:22}}>
            <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:18}}>👤 Mon compte</div>
            <div style={{display:"flex",gap:20,alignItems:"flex-start",flexWrap:"wrap"}}>
              {/* Avatar */}
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
                <div style={{width:80,height:80,borderRadius:"50%",background:profil.avatar_url?"transparent":`${profil.avatar_color}22`,border:`3px solid ${profil.avatar_color}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,fontWeight:800,color:profil.avatar_color,overflow:"hidden",cursor:"pointer",flexShrink:0}}
                  onClick={()=>fileRef.current?.click()}>
                  {profil.avatar_url?<img src={profil.avatar_url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:initials}
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>e.target.files[0]&&handlePhoto(e.target.files[0])}/>
                <button onClick={()=>fileRef.current?.click()} style={{fontSize:11,color:T.accent,background:T.accentDim,border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",fontWeight:600}}>Changer photo</button>
                <div style={{display:"flex",flexWrap:"wrap",gap:5,justifyContent:"center",maxWidth:200}}>
                  {AVATAR_COLORS.map(c=>(
                    <div key={c} onClick={()=>setProfil(p=>({...p,avatar_color:c}))}
                      style={{width:22,height:22,borderRadius:"50%",background:c,cursor:"pointer",border:profil.avatar_color===c?`3px solid ${T.text}`:"3px solid transparent",transition:"transform 0.1s"}}/>
                  ))}
                </div>
              </div>
              {/* Champs */}
              <div style={{flex:1,minWidth:200,display:"flex",flexDirection:"column",gap:11}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div><label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5}}>PRÉNOM</label>{inp("prenom","Lucas")}</div>
                  <div><label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5}}>NOM</label>{inp("nom","Dupont")}</div>
                </div>
                <div><label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5}}>TÉLÉPHONE</label>{inp("telephone","06 ...")}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5}}>NOUVEAU MDP</label>
                    {inp("nouveau","••••••",  "password",pwd,setPwd)}
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5}}>CONFIRMER MDP</label>
                    {inp("confirm","••••••","password",pwd,setPwd)}
                  </div>
                </div>
                {pwdErr&&<div style={{fontSize:12,color:T.danger,fontWeight:600}}>{pwdErr}</div>}
                {(pwd.nouveau||pwd.confirm)&&(
                  <button onClick={changePwd} style={{padding:"9px",borderRadius:9,background:T.warning,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                    🔑 Changer le mot de passe
                  </button>
                )}
              </div>
            </div>
            <button onClick={saveProfil} style={{width:"100%",marginTop:16,padding:"13px",borderRadius:12,background:`linear-gradient(135deg,${T.accent},${T.purple})`,border:"none",color:"#fff",fontSize:15,fontWeight:800,cursor:"pointer"}}>
              ✅ Sauvegarder mon profil
            </button>
          </div>

          {/* Thèmes */}
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:15,padding:22}}>
            <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:18}}>🎨 Thème de l'interface</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12}}>
              {Object.entries(THEMES).map(([id,th])=>(
                <div key={id} onClick={()=>setProfil(p=>({...p,theme_id:id}))}
                  style={{display:"flex",alignItems:"center",gap:14,padding:"14px 16px",borderRadius:13,border:`2px solid ${profil.theme_id===id?T.accent:T.border}`,background:profil.theme_id===id?T.accentDim:"transparent",cursor:"pointer",transition:"all 0.15s"}}>
                  <div style={{display:"flex",gap:4,flexShrink:0}}>
                    <div style={{width:28,height:28,borderRadius:7,background:th.bg,border:`1px solid ${th.border}`}}/>
                    <div style={{width:28,height:28,borderRadius:7,background:th.card,border:`1px solid ${th.border}`}}/>
                    <div style={{width:28,height:28,borderRadius:7,background:th.accent}}/>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:profil.theme_id===id?800:600,color:profil.theme_id===id?T.accent:T.text}}>{th.name}</div>
                    <div style={{fontSize:11,color:T.muted,marginTop:2}}>{th.dark?"Mode sombre":"Mode clair"}</div>
                  </div>
                  {profil.theme_id===id&&<span style={{color:T.accent,fontSize:18,fontWeight:800}}>✓</span>}
                </div>
              ))}
            </div>
            <button onClick={saveProfil} style={{width:"100%",marginTop:18,padding:"13px",borderRadius:12,background:`linear-gradient(135deg,${T.accent},${T.purple})`,border:"none",color:"#fff",fontSize:15,fontWeight:800,cursor:"pointer"}}>
              ✅ Appliquer ce thème
            </button>
          </div>
        </div>
      )}

      {/* ═══ ÉQUIPE & ACCÈS ═══ */}
      {tab==="equipe"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {/* Header */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:17,fontWeight:800,color:T.text}}>👥 Équipe & Accès</div>
              <div style={{fontSize:12,color:T.muted,marginTop:3}}>Gérez les utilisateurs et définissez leurs droits d'accès aux modules</div>
            </div>
            <button onClick={()=>setShowAddUser(true)}
              style={{padding:"10px 20px",borderRadius:11,background:`linear-gradient(135deg,${T.accent},${T.purple})`,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:8}}>
              ✉️ Inviter un utilisateur
            </button>
          </div>

          <div style={{display:"flex",gap:14}}>
            {/* Liste utilisateurs */}
            <div style={{flex:1,minWidth:0}}>
              {/* Recherche */}
              <div style={{display:"flex",alignItems:"center",gap:8,background:T.card,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 13px",marginBottom:10}}>
                <span style={{color:T.muted}}>🔍</span>
                <input value={userSearch} onChange={e=>setUserSearch(e.target.value)} placeholder="Rechercher un utilisateur..."
                  style={{background:"none",border:"none",outline:"none",color:T.text,fontSize:13,flex:1}}/>
                {userSearch&&<button onClick={()=>setUserSearch("")} style={{background:"none",border:"none",color:T.muted,cursor:"pointer"}}>✕</button>}
              </div>

              {/* KPIs */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:10}}>
                {[
                  {lbl:"Total",val:users.length,c:T.accent},
                  {lbl:"Actifs",val:users.filter(u=>u.statut==='actif'||!u.statut).length,c:"#10b981"},
                  {lbl:"Inactifs",val:users.filter(u=>u.statut==='inactif').length,c:"#ef4444"},
                ].map(k=>(
                  <div key={k.lbl} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 14px"}}>
                    <div style={{fontSize:10,color:T.muted,textTransform:"uppercase",letterSpacing:"0.5px"}}>{k.lbl}</div>
                    <div style={{fontSize:22,fontWeight:800,color:k.c}}>{k.val}</div>
                  </div>
                ))}
              </div>

              {/* Table */}
              <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden"}}>
                {loadingUsers?(
                  <div style={{padding:30,textAlign:"center",color:T.muted}}>⏳ Chargement...</div>
                ):(
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead>
                      <tr style={{background:T.bg}}>
                        {["Utilisateur","Rôle","Statut","Accès"].map(c=>(
                          <th key={c} style={{padding:"10px 14px",textAlign:"left",fontSize:10,fontWeight:700,color:T.muted,letterSpacing:"0.7px",textTransform:"uppercase",borderBottom:`1px solid ${T.border}`}}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map(u=>{
                        const isActif = u.statut==='actif'||!u.statut;
                        const isSel = selectedUser?.id===u.id;
                        const role = ROLES.find(r=>r.id===u.role)||{ic:"👤",lbl:u.role};
                        const modCount = u.preferences?.modules ? Object.values(u.preferences.modules).filter(Boolean).length : MODULES_PERMS.length;
                        return(
                          <tr key={u.id}
                            onClick={()=>openUser(u)}
                            style={{borderBottom:`1px solid ${T.border}`,background:isSel?T.accentDim:"transparent",cursor:"pointer",transition:"background 0.1s"}}
                            onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.background=T.bg; }}
                            onMouseLeave={e=>{ if(!isSel) e.currentTarget.style.background="transparent"; }}>
                            <td style={{padding:"12px 14px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:10}}>
                                <div style={{width:34,height:34,borderRadius:"50%",background:`${T.accent}22`,border:`1.5px solid ${T.accent}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:T.accent,flexShrink:0}}>
                                  {(u.prenom?.[0]||"").toUpperCase()}{(u.nom?.[0]||"").toUpperCase()}
                                </div>
                                <div>
                                  <div style={{fontSize:13,fontWeight:600,color:T.text}}>{u.prenom} {u.nom}</div>
                                  <div style={{fontSize:11,color:T.muted}}>{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{padding:"12px 14px"}}>
                              <span style={{fontSize:11,fontWeight:700,padding:"3px 9px",borderRadius:20,background:`${T.accent}15`,color:T.accent}}>
                                {role.ic} {role.lbl}
                              </span>
                            </td>
                            <td style={{padding:"12px 14px"}}>
                              <span style={{fontSize:11,fontWeight:700,padding:"3px 9px",borderRadius:20,background:isActif?"rgba(16,185,129,0.12)":"rgba(239,68,68,0.12)",color:isActif?"#10b981":"#ef4444"}}>
                                {isActif?"✅ Actif":"⛔ Inactif"}
                              </span>
                            </td>
                            <td style={{padding:"12px 14px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:6}}>
                                <div style={{flex:1,height:4,background:T.border,borderRadius:99,overflow:"hidden",maxWidth:60}}>
                                  <div style={{height:"100%",width:`${(modCount/MODULES_PERMS.length)*100}%`,background:T.accent,borderRadius:99}}/>
                                </div>
                                <span style={{fontSize:10,color:T.muted,whiteSpace:"nowrap"}}>{modCount}/{MODULES_PERMS.length}</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Panneau permissions */}
            {selectedUser&&(
              <div style={{width:320,flexShrink:0,background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden",position:"sticky",top:16,maxHeight:"calc(100vh - 120px)",overflowY:"auto"}}>
                {/* Header user */}
                <div style={{padding:"16px 18px",borderBottom:`1px solid ${T.border}`,background:`${T.accent}08`}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:40,height:40,borderRadius:"50%",background:`${T.accent}22`,border:`2px solid ${T.accent}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:T.accent}}>
                        {(selectedUser.prenom?.[0]||"").toUpperCase()}{(selectedUser.nom?.[0]||"").toUpperCase()}
                      </div>
                      <div>
                        <div style={{fontSize:14,fontWeight:700,color:T.text}}>{selectedUser.prenom} {selectedUser.nom}</div>
                        <div style={{fontSize:11,color:T.muted}}>{selectedUser.email}</div>
                      </div>
                    </div>
                    <button onClick={()=>setSelectedUser(null)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:18}}>✕</button>
                  </div>
                  {/* Rôle */}
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:6}}>Rôle</div>
                    <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                      {ROLES.map(r=>(
                        <button key={r.id} onClick={()=>changeRole(selectedUser.id,r.id)}
                          disabled={selectedUser.id===currentUser.id}
                          style={{padding:"5px 10px",borderRadius:8,border:`1.5px solid ${selectedUser.role===r.id?T.accent:T.border}`,background:selectedUser.role===r.id?T.accentDim:"transparent",color:selectedUser.role===r.id?T.accent:T.muted,fontSize:11,fontWeight:selectedUser.role===r.id?700:500,cursor:"pointer",opacity:selectedUser.id===currentUser.id?0.5:1}}>
                          {r.ic} {r.lbl}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Modules */}
                <div style={{padding:"14px 18px"}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>🔒 Accès aux modules</div>
                  <div style={{fontSize:11,color:T.muted,marginBottom:12}}>Activez ou désactivez l'accès à chaque section</div>

                  {/* Tout activer / désactiver */}
                  <div style={{display:"flex",gap:6,marginBottom:10}}>
                    <button onClick={()=>{const m={};MODULES_PERMS.forEach(mod=>m[mod.id]=true);setUserModules(m);}}
                      style={{flex:1,padding:"6px",borderRadius:7,background:`${T.success}15`,border:`1px solid ${T.success}40`,color:"#10b981",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                      ✅ Tout activer
                    </button>
                    <button onClick={()=>{const m={};MODULES_PERMS.forEach(mod=>m[mod.id]=false);setUserModules(m);}}
                      style={{flex:1,padding:"6px",borderRadius:7,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                      🚫 Tout désactiver
                    </button>
                  </div>

                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {MODULES_PERMS.map(mod=>{
                      const enabled = userModules[mod.id] !== false;
                      return(
                        <div key={mod.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 11px",borderRadius:9,border:`1px solid ${enabled?T.accent+"30":T.border}`,background:enabled?`${T.accent}06`:"transparent",transition:"all 0.15s"}}>
                          <span style={{fontSize:16,flexShrink:0}}>{mod.ic}</span>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:600,color:enabled?T.text:T.muted}}>{mod.lbl}</div>
                            <div style={{fontSize:10,color:T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{mod.desc}</div>
                          </div>
                          {/* Toggle */}
                          <div onClick={()=>setUserModules(p=>({...p,[mod.id]:!enabled}))}
                            style={{width:40,height:22,borderRadius:11,background:enabled?T.accent:T.border,position:"relative",cursor:"pointer",transition:"background 0.25s",flexShrink:0}}>
                            <div style={{position:"absolute",top:3,left:enabled?20:3,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left 0.25s",boxShadow:"0 1px 3px rgba(0,0,0,0.25)"}}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button onClick={saveModules} disabled={savingPerms}
                    style={{width:"100%",marginTop:14,padding:"11px",borderRadius:10,background:`linear-gradient(135deg,${T.accent},${T.purple})`,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",opacity:savingPerms?0.7:1}}>
                    {savingPerms?"Sauvegarde...":"💾 Enregistrer les accès"}
                  </button>

                  {/* Réinitialiser mot de passe */}
                  <button onClick={()=>sendResetLink(selectedUser)}
                    style={{width:"100%",marginTop:8,padding:"9px",borderRadius:10,border:`1px solid ${T.accent}40`,background:`${T.accent}10`,color:T.accent,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                    🔑 Envoyer un lien de réinitialisation MDP
                  </button>

                  {/* 2FA */}
                  <div style={{display:"flex",alignItems:"center",gap:10,marginTop:8,padding:"9px 11px",borderRadius:10,border:`1px solid ${T.border}`,background:T.bg}}>
                    <span style={{fontSize:14}}>🔐</span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:600,color:T.text}}>Authentification 2FA</div>
                      <div style={{fontSize:10,color:selectedUser.mfa_enabled?"#10b981":"#ef4444",fontWeight:600}}>{selectedUser.mfa_enabled?"Activée":"Désactivée"}</div>
                    </div>
                    {selectedUser.mfa_enabled&&selectedUser.id!==currentUser.id&&(
                      <button onClick={()=>disableMFA(selectedUser)}
                        style={{padding:"4px 10px",borderRadius:7,border:"1px solid rgba(239,68,68,0.3)",background:"rgba(239,68,68,0.08)",color:"#ef4444",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                        Désactiver
                      </button>
                    )}
                  </div>

                  {/* Activer / désactiver compte */}
                  {selectedUser.id!==currentUser.id&&(
                    <button onClick={()=>toggleUserActif(selectedUser)}
                      style={{width:"100%",marginTop:8,padding:"9px",borderRadius:10,border:`1px solid ${selectedUser.statut==='inactif'?"#10b981":"#ef4444"}40`,background:`${selectedUser.statut==='inactif'?"#10b981":"#ef4444"}10`,color:selectedUser.statut==='inactif'?"#10b981":"#ef4444",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                      {selectedUser.statut==='inactif'?"↩️ Réactiver le compte":"⛔ Désactiver le compte"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ MODÈLES PV ═══ */}
      {tab==="modeles"&&(
        <div style={{display:"flex",gap:16}}>
          <div style={{width:210,flexShrink:0,display:"flex",flexDirection:"column",gap:8}}>
            {Object.entries(modeles).map(([k,mod])=>(
              <div key={k} onClick={()=>setSelectedModele(k)}
                style={{background:selectedModele===k?T.accentDim:T.card,border:`1.5px solid ${selectedModele===k?T.accent:T.border}`,borderRadius:11,padding:"11px 13px",cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:9}}>
                  <div style={{width:32,height:32,borderRadius:8,background:`${mod.color}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17}}>{mod.icon}</div>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:selectedModele===k?T.accent:T.text}}>{mod.nom}</div>
                    <div style={{fontSize:10,color:T.muted}}>{mod.sections.length} sections</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{flex:1,display:"flex",gap:14}}>
            <div style={{flex:1,background:T.card,border:`1px solid ${T.border}`,borderRadius:15,overflow:"hidden"}}>
              <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.border}`,background:T.bg}}>
                <div style={{fontSize:14,fontWeight:700,color:T.text}}>{m.icon} {m.nom}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>⋮⋮ Glissez · ✕ Retirer</div>
              </div>
              <div style={{padding:12,display:"flex",flexDirection:"column",gap:8}}>
                {m.sections.map((sId,si)=>{
                  const sec=SECTIONS_DISPONIBLES.find(s=>s.id===sId);
                  if(!sec) return null;
                  return(
                    <div key={sId} draggable
                      onDragStart={()=>setDragging(si)} onDragOver={e=>{e.preventDefault();setDragOver(si);}}
                      onDrop={()=>{if(dragging!==null){moveSection(dragging,si);setDragging(null);setDragOver(null);}}}
                      onDragEnd={()=>{setDragging(null);setDragOver(null);}}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:dragOver===si?T.accentDim:T.bg,border:`1.5px solid ${dragOver===si?T.accent:T.border}`,borderRadius:10,cursor:"grab"}}>
                      <span style={{color:T.muted}}>⋮⋮</span>
                      <span style={{fontSize:18}}>{sec.ic}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:700,color:T.text}}>{sec.nom}</div>
                        <div style={{fontSize:10,color:T.muted}}>{sec.desc}</div>
                      </div>
                      <span style={{fontSize:11,color:T.muted,background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"1px 8px"}}>#{si+1}</span>
                      <button onClick={()=>removeSection(sId)} style={{background:"none",border:"none",color:T.danger,cursor:"pointer",fontSize:18,fontWeight:800}}>✕</button>
                    </div>
                  );
                })}
              </div>
              <div style={{padding:"0 12px 12px"}}>
                <button onClick={saveModeles} style={{width:"100%",padding:"10px",borderRadius:10,background:`linear-gradient(135deg,${T.accent},${T.purple})`,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                  💾 Sauvegarder ce modèle
                </button>
              </div>
            </div>
            <div style={{width:220,flexShrink:0,background:T.card,border:`1px solid ${T.border}`,borderRadius:15,overflow:"hidden"}}>
              <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.border}`,background:T.bg}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>+ Ajouter</div>
              </div>
              <div style={{padding:10,display:"flex",flexDirection:"column",gap:6}}>
                {SECTIONS_DISPONIBLES.map(sec=>{
                  const active=m.sections.includes(sec.id);
                  return(
                    <div key={sec.id} onClick={()=>!active&&addSection(sec.id)}
                      style={{display:"flex",alignItems:"center",gap:9,padding:"9px 11px",borderRadius:9,border:`1px solid ${active?T.success+"40":T.border}`,background:active?`${T.success}10`:T.bg,cursor:active?"default":"pointer",opacity:active?0.55:1}}>
                      <span style={{fontSize:17}}>{sec.ic}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:11,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{sec.nom}</div>
                      </div>
                      {active?<span style={{color:T.success,fontWeight:700}}>✓</span>:<span style={{color:T.accent,fontWeight:700}}>+</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ AUTOMATISATIONS ═══ */}
      {tab==="automations"&&<AutomationsView T={T}/>}

      {/* ═══ ALERTES / AGENDA ═══ */}
      {tab==="agenda"&&<ICalExport T={T}/>}
      {tab==="alertes"&&(
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:15,padding:22}}>
          <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:16}}>🔔 Notifications</div>
          {[["notif_retard","⏰ Alerte retard","Quand un technicien dépasse la durée estimée"],["notif_sav","🎫 Alerte SAV urgent","Nouveau ticket SAV urgent"]].map(([key,label,desc])=>{
            const val = JSON.parse(localStorage.getItem("gs_settings")||"{}")[key]!==false;
            return(
              <div key={key} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 16px",background:T.bg,borderRadius:11,border:`1px solid ${T.border}`,marginBottom:10}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.text}}>{label}</div>
                  <div style={{fontSize:11,color:T.muted,marginTop:3}}>{desc}</div>
                </div>
                <div onClick={()=>{
                  const s=JSON.parse(localStorage.getItem("gs_settings")||"{}");
                  s[key]=!val; localStorage.setItem("gs_settings",JSON.stringify(s));
                  saveOk("✅ Sauvegardé");
                }} style={{width:48,height:26,borderRadius:13,background:val?T.success:T.border,position:"relative",cursor:"pointer",transition:"background 0.3s",flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:val?22:3,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left 0.3s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
                </div>
              </div>
            );
          })}
        </div>
      )}

        </div>{/* end content area */}
      </div>{/* end layout */}

      {/* ── Modal invitation ── */}
      {showAddUser&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
          onClick={e=>{if(e.target===e.currentTarget)setShowAddUser(false);}}>
          <div style={{background:T.card,borderRadius:16,width:"100%",maxWidth:440,boxShadow:"0 24px 64px rgba(0,0,0,0.3)"}}>
            <div style={{padding:"18px 22px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:16,fontWeight:800,color:T.text}}>✉️ Inviter un utilisateur</div>
              <button onClick={()=>setShowAddUser(false)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:20}}>✕</button>
            </div>
            <div style={{padding:"20px 22px",display:"flex",flexDirection:"column",gap:14}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5}}>PRÉNOM *</label>
                  <input value={addForm.prenom} onChange={e=>setAddForm(p=>({...p,prenom:e.target.value}))} placeholder="Prénom"
                    style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",width:"100%"}}/>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5}}>NOM *</label>
                  <input value={addForm.nom} onChange={e=>setAddForm(p=>({...p,nom:e.target.value}))} placeholder="Nom"
                    style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",width:"100%"}}/>
                </div>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5}}>EMAIL *</label>
                <input value={addForm.email} onChange={e=>setAddForm(p=>({...p,email:e.target.value}))} placeholder="prenom.nom@entreprise.fr" type="email"
                  style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",width:"100%"}}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5}}>TÉLÉPHONE</label>
                <input value={addForm.telephone} onChange={e=>setAddForm(p=>({...p,telephone:e.target.value}))} placeholder="06 00 00 00 00"
                  style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",width:"100%"}}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:8}}>RÔLE</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {ROLES.map(r=>(
                    <button key={r.id} onClick={()=>setAddForm(p=>({...p,role:r.id}))}
                      style={{padding:"7px 12px",borderRadius:8,border:`1.5px solid ${addForm.role===r.id?T.accent:T.border}`,background:addForm.role===r.id?T.accentDim:"transparent",color:addForm.role===r.id?T.accent:T.muted,fontSize:12,fontWeight:addForm.role===r.id?700:500,cursor:"pointer"}}>
                      {r.ic} {r.lbl}
                    </button>
                  ))}
                </div>
              </div>
              {addErr&&<div style={{fontSize:12,color:"#ef4444",fontWeight:600}}>{addErr}</div>}
            </div>
            <div style={{padding:"14px 22px",borderTop:`1px solid ${T.border}`,display:"flex",gap:8}}>
              <button onClick={()=>setShowAddUser(false)} style={{flex:1,padding:"10px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,fontSize:13,cursor:"pointer"}}>Annuler</button>
              <button onClick={inviteUser} disabled={savingAdd||!addForm.email||!addForm.prenom||!addForm.nom}
                style={{flex:2,padding:"10px",borderRadius:9,background:`linear-gradient(135deg,${T.accent},${T.purple})`,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",opacity:savingAdd?0.7:1}}>
                {savingAdd?"Envoi...":"✉️ Envoyer l'invitation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ API & INTÉGRATIONS ═══ */}
      {isAdmin&&tab==="api"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{background:T.card,border:`2px solid ${T.accent}`,borderRadius:15,padding:24}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <div style={{fontSize:15,fontWeight:700,color:T.text}}>🤖 Intelligence Artificielle</div>
              {(aiCfg?.hasOpenaiKey||aiCfg?.hasAnthropicKey) && <span style={{fontSize:10,fontWeight:800,color:"#fff",background:T.success,borderRadius:6,padding:"2px 8px"}}>ACTIVÉ</span>}
            </div>
            <div style={{fontSize:13,color:T.muted,marginBottom:18}}>
              Clés API utilisées par l'assistant IA : synthèse des comptes-rendus, détection du matériel utilisé (OpenAI), et suggestions de reformulation d'email (Anthropic).
              {aiCfg?.source==="env" && <div style={{marginTop:8,color:"#f59e0b",fontWeight:600}}>⚠️ Configuration actuelle issue des variables d'environnement (héritée) — l'enregistrer ici la reprend en base.</div>}
            </div>

            {loadingAi ? <div style={{fontSize:13,color:T.muted}}>Chargement…</div> : (
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  <label style={{flex:"1 1 300px",display:"flex",flexDirection:"column",gap:5}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Clé OpenAI {aiCfg?.hasOpenaiKey && <span style={{fontWeight:400,color:T.muted}}>(laisser vide pour conserver)</span>}</span>
                    <input type="password" value={aiForm.openaiKey} onChange={e=>setAiForm(f=>({...f,openaiKey:e.target.value}))} placeholder={aiCfg?.hasOpenaiKey?"••••••••••••••••":"sk-..."}
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none",fontFamily:"monospace"}}/>
                  </label>
                  <label style={{flex:"1 1 300px",display:"flex",flexDirection:"column",gap:5}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Clé Anthropic {aiCfg?.hasAnthropicKey && <span style={{fontWeight:400,color:T.muted}}>(laisser vide pour conserver)</span>}</span>
                    <input type="password" value={aiForm.anthropicKey} onChange={e=>setAiForm(f=>({...f,anthropicKey:e.target.value}))} placeholder={aiCfg?.hasAnthropicKey?"••••••••••••••••":"sk-ant-..."}
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none",fontFamily:"monospace"}}/>
                  </label>
                </div>

                {aiTestResult && (
                  <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:12,display:"flex",flexDirection:"column",gap:6}}>
                    {aiTestResult.error && <div style={{fontSize:12.5,fontWeight:700,color:"#ef4444"}}>❌ {aiTestResult.error}</div>}
                    {aiTestResult.openai && (
                      <div style={{fontSize:12.5,fontWeight:700,color:aiTestResult.openai.ok?T.success:"#ef4444"}}>
                        {aiTestResult.openai.ok?"✅ OpenAI : connexion réussie":`❌ OpenAI : ${aiTestResult.openai.error}`}
                      </div>
                    )}
                    {aiTestResult.anthropic && (
                      <div style={{fontSize:12.5,fontWeight:700,color:aiTestResult.anthropic.ok?T.success:"#ef4444"}}>
                        {aiTestResult.anthropic.ok?"✅ Anthropic : connexion réussie":`❌ Anthropic : ${aiTestResult.anthropic.error}`}
                      </div>
                    )}
                  </div>
                )}

                <div style={{display:"flex",gap:10}}>
                  <button onClick={testAiConfig} disabled={testingAi||(!aiForm.openaiKey&&!aiForm.anthropicKey&&!aiCfg?.hasOpenaiKey&&!aiCfg?.hasAnthropicKey)}
                    style={{padding:"10px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:13,fontWeight:700,cursor:testingAi?"wait":"pointer"}}>
                    {testingAi?"Test en cours…":"🔍 Tester la connexion"}
                  </button>
                  <button onClick={saveAiConfig} disabled={savingAi||(!aiForm.openaiKey&&!aiForm.anthropicKey)}
                    style={{padding:"10px 16px",borderRadius:9,border:"none",background:T.accent,color:"#fff",fontSize:13,fontWeight:700,cursor:savingAi?"wait":"pointer",opacity:(!aiForm.openaiKey&&!aiForm.anthropicKey)?0.5:1}}>
                    {savingAi?"Enregistrement…":"💾 Enregistrer"}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={{background:T.card,border:`2px solid ${T.accent}`,borderRadius:15,padding:24}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <div style={{fontSize:15,fontWeight:700,color:T.text}}>🛰️ GPS Flotte — API Océan</div>
              {oceanCfg?.enabled && <span style={{fontSize:10,fontWeight:800,color:"#fff",background:T.success,borderRadius:6,padding:"2px 8px"}}>ACTIVÉ</span>}
            </div>
            <div style={{fontSize:13,color:T.muted,marginBottom:18}}>
              Identifiants du compte <a href="http://api-docs.oceansystem.com/fr/home" target="_blank" rel="noreferrer" style={{color:T.accent}}>oceansystem.com</a> utilisés pour récupérer en temps réel la position, la plaque, le nom du conducteur et le niveau de carburant des véhicules — affichés sur la page <strong>GPS Flotte</strong>.
              {oceanCfg?.source==="env" && <div style={{marginTop:8,color:"#f59e0b",fontWeight:600}}>⚠️ Configuration actuelle issue des variables d'environnement (héritée) — l'enregistrer ici la reprend en base.</div>}
            </div>

            {loadingOcean ? <div style={{fontSize:13,color:T.muted}}>Chargement…</div> : (
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  <label style={{flex:"1 1 260px",display:"flex",flexDirection:"column",gap:5}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>URL de base</span>
                    <input value={oceanForm.baseUrl} onChange={e=>setOceanForm(f=>({...f,baseUrl:e.target.value}))} placeholder="https://v3.oceansystem.com/ocean/restapi"
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none"}}/>
                  </label>
                  <label style={{flex:"1 1 180px",display:"flex",flexDirection:"column",gap:5}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Identifiant</span>
                    <input value={oceanForm.username} onChange={e=>setOceanForm(f=>({...f,username:e.target.value}))} placeholder="identifiant Océan"
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none"}}/>
                  </label>
                  <label style={{flex:"1 1 180px",display:"flex",flexDirection:"column",gap:5}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Mot de passe {oceanCfg?.hasPassword && <span style={{fontWeight:400,color:T.muted}}>(laisser vide pour conserver)</span>}</span>
                    <input type="password" value={oceanForm.password} onChange={e=>setOceanForm(f=>({...f,password:e.target.value}))} placeholder={oceanCfg?.hasPassword?"••••••••":"mot de passe"}
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none"}}/>
                  </label>
                </div>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  <label style={{flex:"1 1 260px",display:"flex",flexDirection:"column",gap:5}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Chemin de connexion (login)</span>
                    <input value={oceanForm.loginPath} onChange={e=>setOceanForm(f=>({...f,loginPath:e.target.value}))} placeholder="/auth/authenticate2"
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none",fontFamily:"monospace"}}/>
                  </label>
                  <label style={{flex:"1 1 260px",display:"flex",flexDirection:"column",gap:5}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Chemin des positions véhicules</span>
                    <input value={oceanForm.positionsPath} onChange={e=>setOceanForm(f=>({...f,positionsPath:e.target.value}))} placeholder="/mobility/v1/vehiclePositions"
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none",fontFamily:"monospace"}}/>
                  </label>
                </div>

                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none"}}>
                  <input type="checkbox" checked={oceanForm.enabled} onChange={e=>setOceanForm(f=>({...f,enabled:e.target.checked}))}/>
                  <span style={{fontSize:13,color:T.text}}>Activer la synchronisation GPS (polling automatique)</span>
                </label>

                <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:12}}>
                  <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none",marginBottom:oceanForm.privacyEnabled?10:0}}>
                    <input type="checkbox" checked={oceanForm.privacyEnabled} onChange={e=>setOceanForm(f=>({...f,privacyEnabled:e.target.checked}))}/>
                    <span style={{fontSize:13,color:T.text,fontWeight:600}}>🔒 Coupure vie privée automatique</span>
                  </label>
                  <div style={{fontSize:11.5,color:T.muted,marginBottom:oceanForm.privacyEnabled?10:0}}>
                    Sur cette plage horaire, la synchro Océan est totalement arrêtée et aucune position n'est conservée ni affichée (les techniciens restent suivis normalement côté Océan, seule notre appli s'interrompt).
                  </div>
                  {oceanForm.privacyEnabled && (
                    <div style={{display:"flex",gap:10,alignItems:"center"}}>
                      <label style={{display:"flex",flexDirection:"column",gap:4}}>
                        <span style={{fontSize:10.5,fontWeight:700,color:T.muted}}>Coupure à partir de</span>
                        <input type="time" value={oceanForm.privacyStart} onChange={e=>setOceanForm(f=>({...f,privacyStart:e.target.value}))}
                          style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 10px",color:T.text,fontSize:13,outline:"none"}}/>
                      </label>
                      <span style={{color:T.muted,marginTop:14}}>→</span>
                      <label style={{display:"flex",flexDirection:"column",gap:4}}>
                        <span style={{fontSize:10.5,fontWeight:700,color:T.muted}}>Reprise à</span>
                        <input type="time" value={oceanForm.privacyEnd} onChange={e=>setOceanForm(f=>({...f,privacyEnd:e.target.value}))}
                          style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 10px",color:T.text,fontSize:13,outline:"none"}}/>
                      </label>
                    </div>
                  )}
                </div>

                {oceanTestResult && (
                  <div style={{background:T.bg,border:`1px solid ${oceanTestResult.ok?T.success:"#ef4444"}`,borderRadius:10,padding:12}}>
                    <div style={{fontSize:12.5,fontWeight:700,color:oceanTestResult.ok?T.success:"#ef4444",marginBottom:6}}>
                      {oceanTestResult.ok ? "✅ Connexion réussie" : "❌ Échec"}
                      {oceanTestResult.error && ` — ${oceanTestResult.error}`}
                    </div>
                    {oceanTestResult.positions && (
                      <div style={{fontSize:11,color:T.muted,marginBottom:6}}>Endpoint positions : statut {oceanTestResult.positions.status}{oceanTestResult.positions.count!=null?`, ${oceanTestResult.positions.count} véhicule(s)`:""}</div>
                    )}
                    {(oceanTestResult.positions?.sample || oceanTestResult.login?.sample) && (
                      <pre style={{fontSize:11,color:T.text,margin:0,overflowX:"auto",maxHeight:180,fontFamily:"monospace"}}>
                        {JSON.stringify(oceanTestResult.positions?.sample ?? oceanTestResult.login?.sample, null, 2)}
                      </pre>
                    )}
                    <div style={{fontSize:10.5,color:T.muted,marginTop:6}}>Réponse brute du serveur Océan — utile pour ajuster les noms de champs (plaque, carburant…) si besoin.</div>
                  </div>
                )}

                <div style={{display:"flex",gap:10}}>
                  <button onClick={testOceanConfig} disabled={testingOcean||!oceanForm.baseUrl||!oceanForm.username}
                    style={{padding:"10px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:13,fontWeight:700,cursor:testingOcean?"wait":"pointer"}}>
                    {testingOcean?"Test en cours…":"🔍 Tester la connexion"}
                  </button>
                  <button onClick={saveOceanConfig} disabled={savingOcean||!oceanForm.baseUrl||!oceanForm.username}
                    style={{padding:"10px 16px",borderRadius:9,border:"none",background:`linear-gradient(135deg,${T.accent},${T.purple})`,color:"#fff",fontSize:13,fontWeight:700,cursor:savingOcean?"wait":"pointer"}}>
                    {savingOcean?"Enregistrement…":"💾 Enregistrer"}
                  </button>
                </div>
                {oceanCfg?.updatedAt && <div style={{fontSize:11,color:T.muted}}>Dernière modification le {new Date(oceanCfg.updatedAt).toLocaleString('fr-FR')}</div>}
              </div>
            )}
          </div>

          <div style={{background:T.card,border:`2px solid ${T.accent}`,borderRadius:15,padding:24}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <div style={{fontSize:15,fontWeight:700,color:T.text}}>🧾 Odoo — Facturation</div>
              {odooCfg?.enabled && <span style={{fontSize:10,fontWeight:800,color:"#fff",background:T.success,borderRadius:6,padding:"2px 8px"}}>ACTIVÉ</span>}
            </div>
            <div style={{fontSize:13,color:T.muted,marginBottom:18}}>
              Identifiants de ton instance Odoo (URL, base de données, utilisateur et <strong>clé API</strong> — générée dans Odoo via Réglages &gt; Compte &gt; Sécurité &gt; Clés API) utilisés pour synchroniser la clôture des dossiers d'intervention avec la facturation.
            </div>

            {loadingOdoo ? <div style={{fontSize:13,color:T.muted}}>Chargement…</div> : (
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  <label style={{flex:"1 1 260px",display:"flex",flexDirection:"column",gap:5}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>URL de l'instance Odoo</span>
                    <input value={odooForm.baseUrl} onChange={e=>setOdooForm(f=>({...f,baseUrl:e.target.value}))} placeholder="https://monentreprise.odoo.com"
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none"}}/>
                  </label>
                  <label style={{flex:"1 1 180px",display:"flex",flexDirection:"column",gap:5}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Base de données</span>
                    <input value={odooForm.database} onChange={e=>setOdooForm(f=>({...f,database:e.target.value}))} placeholder="monentreprise"
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none"}}/>
                  </label>
                </div>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  <label style={{flex:"1 1 180px",display:"flex",flexDirection:"column",gap:5}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Utilisateur (email Odoo)</span>
                    <input value={odooForm.username} onChange={e=>setOdooForm(f=>({...f,username:e.target.value}))} placeholder="admin@monentreprise.fr"
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none"}}/>
                  </label>
                  <label style={{flex:"1 1 180px",display:"flex",flexDirection:"column",gap:5}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Clé API {odooCfg?.hasApiKey && <span style={{fontWeight:400,color:T.muted}}>(laisser vide pour conserver)</span>}</span>
                    <input type="password" value={odooForm.apiKey} onChange={e=>setOdooForm(f=>({...f,apiKey:e.target.value}))} placeholder={odooCfg?.hasApiKey?"••••••••":"clé API Odoo"}
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none"}}/>
                  </label>
                </div>

                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none"}}>
                  <input type="checkbox" checked={odooForm.enabled} onChange={e=>setOdooForm(f=>({...f,enabled:e.target.checked}))}/>
                  <span style={{fontSize:13,color:T.text}}>Activer la synchronisation Odoo</span>
                </label>

                {odooTestResult && (
                  <div style={{background:T.bg,border:`1px solid ${odooTestResult.ok?T.success:"#ef4444"}`,borderRadius:10,padding:12}}>
                    <div style={{fontSize:12.5,fontWeight:700,color:odooTestResult.ok?T.success:"#ef4444",marginBottom:6}}>
                      {odooTestResult.ok ? "✅ Connexion réussie" : "❌ Échec"}
                      {odooTestResult.error && ` — ${odooTestResult.error}`}
                    </div>
                    {odooTestResult.ok && (
                      <div style={{fontSize:11,color:T.muted}}>Odoo {odooTestResult.serverVersion || "?"} — {odooTestResult.partnerCount ?? "?"} contact(s) trouvé(s)</div>
                    )}
                  </div>
                )}

                <div style={{display:"flex",gap:10}}>
                  <button onClick={testOdooConfig} disabled={testingOdoo||!odooForm.baseUrl||!odooForm.database||!odooForm.username}
                    style={{padding:"10px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:13,fontWeight:700,cursor:testingOdoo?"wait":"pointer"}}>
                    {testingOdoo?"Test en cours…":"🔍 Tester la connexion"}
                  </button>
                  <button onClick={saveOdooConfig} disabled={savingOdoo||!odooForm.baseUrl||!odooForm.database||!odooForm.username}
                    style={{padding:"10px 16px",borderRadius:9,border:"none",background:`linear-gradient(135deg,${T.accent},${T.purple})`,color:"#fff",fontSize:13,fontWeight:700,cursor:savingOdoo?"wait":"pointer"}}>
                    {savingOdoo?"Enregistrement…":"💾 Enregistrer"}
                  </button>
                </div>
                {odooCfg?.updatedAt && <div style={{fontSize:11,color:T.muted}}>Dernière modification le {new Date(odooCfg.updatedAt).toLocaleString('fr-FR')}</div>}

                {odooCfg?.enabled && (
                  <div style={{borderTop:`1px solid ${T.border}`,paddingTop:14,marginTop:2}}>
                    <div style={{fontSize:12.5,fontWeight:700,color:T.text,marginBottom:6}}>🚗 Parc automobile</div>
                    <div style={{fontSize:11.5,color:T.muted,marginBottom:10}}>
                      Crée/met à jour la fiche Odoo Fleet de chaque véhicule (marque, modèle, kilométrage) et rattache le conducteur à un employé Odoo <strong>existant</strong> trouvé par nom (technicien assigné, sinon titulaire) — aucun contact n'est créé côté Odoo depuis le CRM.
                    </div>
                    {fleetSyncResult && (
                      <div style={{background:T.bg,border:`1px solid ${fleetSyncResult.ok?T.success:"#ef4444"}`,borderRadius:10,padding:12,marginBottom:10}}>
                        {fleetSyncResult.ok ? (
                          <>
                            <div style={{fontSize:12.5,fontWeight:700,color:T.success,marginBottom:6}}>✅ {fleetSyncResult.synced}/{fleetSyncResult.total} véhicule(s) synchronisé(s)</div>
                            {fleetSyncResult.failed>0 && (
                              <div style={{fontSize:11,color:"#ef4444"}}>
                                {fleetSyncResult.details.filter(d=>!d.ok).map((d,i)=><div key={i}>{d.immatriculation} : {d.error}</div>)}
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={{fontSize:12.5,fontWeight:700,color:"#ef4444"}}>❌ {fleetSyncResult.error}</div>
                        )}
                      </div>
                    )}
                    <button onClick={syncFleet} disabled={syncingFleet}
                      style={{padding:"10px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:13,fontWeight:700,cursor:syncingFleet?"wait":"pointer"}}>
                      {syncingFleet?"Synchronisation…":"🔄 Synchroniser le parc automobile"}
                    </button>
                  </div>
                )}

                <div style={{borderTop:`1px solid ${T.border}`,paddingTop:14,marginTop:2}}>
                  <div style={{fontSize:12.5,fontWeight:700,color:T.text,marginBottom:6}}>✍️ Signature électronique</div>
                  <div style={{fontSize:11.5,color:T.muted,marginBottom:10}}>
                    Télécharge un exemple de la fiche de clôture (page fixe, contenu identique à chaque intervention) — à uploader une seule fois dans Odoo &gt; app Signature &gt; Modèles, avec deux champs signature nommés exactement <strong>TECHNICIEN</strong> et <strong>CLIENT</strong>.
                  </div>
                  <label style={{display:"flex",flexDirection:"column",gap:5,marginBottom:12}}>
                    <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Nom du modèle dans Odoo Sign</span>
                    <input value={odooForm.signTemplateName} onChange={e=>setOdooForm(f=>({...f,signTemplateName:e.target.value}))} placeholder="fiche-signature-exemple.pdf"
                      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none",maxWidth:340}}/>
                  </label>
                  <button onClick={async()=>{
                    try {
                      const res = await fetch(`${BASE}/pdf/generate/954eab27-2397-4f50-a56c-74d551833306?modele=fiche_signature`, { headers:h() });
                      if(!res.ok) throw new Error(`Erreur ${res.status}`);
                      const blob = await res.blob();
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = "fiche-signature-exemple.pdf";
                      a.click();
                      URL.revokeObjectURL(a.href);
                    } catch(e) { alert("Erreur de téléchargement : "+e.message); }
                  }} style={{padding:"10px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:13,fontWeight:700,cursor:"pointer"}}>
                    📄 Télécharger la fiche de signature (exemple)
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={{background:T.card,border:`2px solid ${T.accent}`,borderRadius:15,padding:24}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <div style={{fontSize:15,fontWeight:700,color:T.text}}>🚦 Trafic — TomTom</div>
              {tomtomCfg?.enabled && <span style={{fontSize:10,fontWeight:800,color:"#fff",background:T.success,borderRadius:6,padding:"2px 8px"}}>ACTIVÉ</span>}
            </div>
            <div style={{fontSize:13,color:T.muted,marginBottom:18}}>
              Clé API <a href="https://developer.tomtom.com/" target="_blank" rel="noreferrer" style={{color:T.accent}}>developer.tomtom.com</a> utilisée pour afficher le trafic temps réel sur la carte et calculer le temps de trajet des techniciens vers leurs interventions.
            </div>

            {loadingTomtom ? <div style={{fontSize:13,color:T.muted}}>Chargement…</div> : (
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <label style={{display:"flex",flexDirection:"column",gap:5}}>
                  <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Clé API {tomtomCfg?.hasApiKey && <span style={{fontWeight:400,color:T.muted}}>(laisser vide pour conserver)</span>}</span>
                  <input type="password" value={tomtomForm.apiKey} onChange={e=>setTomtomForm(f=>({...f,apiKey:e.target.value}))} placeholder={tomtomCfg?.hasApiKey?"••••••••":"clé API TomTom"}
                    style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none",maxWidth:340}}/>
                </label>

                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none"}}>
                  <input type="checkbox" checked={tomtomForm.enabled} onChange={e=>setTomtomForm(f=>({...f,enabled:e.target.checked}))}/>
                  <span style={{fontSize:13,color:T.text}}>Activer le trafic temps réel</span>
                </label>

                {tomtomTestResult && (
                  <div style={{background:T.bg,border:`1px solid ${tomtomTestResult.ok?T.success:"#ef4444"}`,borderRadius:10,padding:12}}>
                    <div style={{fontSize:12.5,fontWeight:700,color:tomtomTestResult.ok?T.success:"#ef4444"}}>
                      {tomtomTestResult.ok ? `✅ Connexion réussie${tomtomTestResult.sample?" — "+tomtomTestResult.sample:""}` : `❌ ${tomtomTestResult.error}`}
                    </div>
                  </div>
                )}

                <div style={{display:"flex",gap:10}}>
                  <button onClick={testTomtomConfig} disabled={testingTomtom||!tomtomForm.apiKey&&!tomtomCfg?.hasApiKey}
                    style={{padding:"10px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:13,fontWeight:700,cursor:testingTomtom?"wait":"pointer"}}>
                    {testingTomtom?"Test en cours…":"🔍 Tester la connexion"}
                  </button>
                  <button onClick={saveTomtomConfig} disabled={savingTomtom}
                    style={{padding:"10px 16px",borderRadius:9,border:"none",background:`linear-gradient(135deg,${T.accent},${T.purple})`,color:"#fff",fontSize:13,fontWeight:700,cursor:savingTomtom?"wait":"pointer"}}>
                    {savingTomtom?"Enregistrement…":"💾 Enregistrer"}
                  </button>
                </div>
                {tomtomCfg?.updatedAt && <div style={{fontSize:11,color:T.muted}}>Dernière modification le {new Date(tomtomCfg.updatedAt).toLocaleString('fr-FR')}</div>}

                <div style={{borderTop:`1px solid ${T.border}`,paddingTop:14,marginTop:2}}>
                  <div style={{fontSize:12.5,fontWeight:700,color:T.text,marginBottom:6}}>🏢 Adresse du siège / dépôt</div>
                  <div style={{fontSize:11.5,color:T.muted,marginBottom:10}}>
                    Point de départ utilisé pour calculer automatiquement la distance, le temps de trajet et l'heure d'arrivée estimée vers chaque client à la planification d'une intervention.
                  </div>
                  {depotError&&<div style={{fontSize:12,color:"#ef4444",fontWeight:600,marginBottom:8}}>⚠ {depotError}</div>}
                  <div style={{display:"flex",gap:8}}>
                    <input value={depotAdresse} onChange={e=>setDepotAdresse(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveDepot()} placeholder="ex: 12 Rue des Pyrénées, 68350 Brunstatt-Didenheim"
                      style={{flex:1,maxWidth:360,background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none"}}/>
                    <button onClick={saveDepot} disabled={savingDepot||!depotAdresse.trim()}
                      style={{padding:"9px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:13,fontWeight:700,cursor:savingDepot?"wait":"pointer"}}>
                      {savingDepot?"Recherche…":"💾 Enregistrer"}
                    </button>
                  </div>
                  {tomtomCfg?.depotLat && <div style={{fontSize:11,color:T.muted,marginTop:8}}>📍 Position enregistrée : {tomtomCfg.depotLat.toFixed(4)}, {tomtomCfg.depotLng.toFixed(4)}</div>}
                </div>

                <div style={{borderTop:`1px solid ${T.border}`,paddingTop:14,marginTop:2}}>
                  <div style={{fontSize:12.5,fontWeight:700,color:T.text,marginBottom:6}}>📍 Zones surveillées</div>
                  <div style={{fontSize:11.5,color:T.muted,marginBottom:10}}>
                    Villes/zones mises en évidence sur la carte (ex: Colmar, Belfort) — tape un nom de ville, la position est résolue automatiquement.
                  </div>
                  {zoneError&&<div style={{fontSize:12,color:"#ef4444",fontWeight:600,marginBottom:8}}>⚠ {zoneError}</div>}
                  <div style={{display:"flex",gap:8,marginBottom:12}}>
                    <input value={newZoneName} onChange={e=>setNewZoneName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addTomtomZone()} placeholder="ex: Colmar"
                      style={{flex:1,maxWidth:240,background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none"}}/>
                    <button onClick={addTomtomZone} disabled={addingZone||!newZoneName.trim()}
                      style={{padding:"9px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:13,fontWeight:700,cursor:addingZone?"wait":"pointer"}}>
                      {addingZone?"Recherche…":"➕ Ajouter"}
                    </button>
                  </div>
                  {tomtomZones.length>0 && (
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {tomtomZones.map(z=>(
                        <div key={z.id} style={{display:"flex",alignItems:"center",gap:10,background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"8px 12px"}}>
                          <span style={{fontSize:13,fontWeight:700,color:T.text,flex:1}}>📍 {z.nom}</span>
                          <span style={{fontSize:11,color:T.muted}}>{Number(z.lat).toFixed(3)}, {Number(z.lng).toFixed(3)} · rayon {z.rayon_km}km</span>
                          <button onClick={()=>removeTomtomZone(z.id)} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:15}}>🗑</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {oceanCfg?.enabled && (
            <div style={{background:T.card,border:`2px solid ${T.accent}`,borderRadius:15,padding:24}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <div style={{fontSize:15,fontWeight:700,color:T.text}}>🛣️ Relevé kilométrique automatique</div>
              </div>
              <div style={{fontSize:13,color:T.muted,marginBottom:18}}>
                Chaque lundi matin (6h-9h), le kilométrage de chaque véhicule est récupéré automatiquement via Océan et ajouté à son historique — visible dans <strong>Parc véhicules</strong> et le <strong>Dashboard km</strong>, avec le badge 🛰️ Auto.
              </div>

              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div style={{fontSize:12.5,color:T.text}}>
                  {kmSyncStatus?.lastResult
                    ? <>Dernière synchro le {new Date(kmSyncStatus.lastResult.ranAt).toLocaleString('fr-FR')} — {kmSyncStatus.lastResult.synced} véhicule(s) mis à jour{kmSyncStatus.lastResult.skipped ? `, ${kmSyncStatus.lastResult.skipped} sans correspondance` : ""}.</>
                    : "Aucune synchro automatique n'a encore eu lieu."}
                </div>

                {kmSyncResult && (
                  <div style={{background:T.bg,border:`1px solid ${kmSyncResult.ok?T.success:"#ef4444"}`,borderRadius:10,padding:12,fontSize:12.5,color:kmSyncResult.ok?T.success:"#ef4444",fontWeight:600}}>
                    {kmSyncResult.ok
                      ? `✅ ${kmSyncResult.synced} véhicule(s) synchronisé(s)${kmSyncResult.skipped ? `, ${kmSyncResult.skipped} sans correspondance` : ""} (sur ${kmSyncResult.total} reçus d'Océan).`
                      : `❌ Échec — ${kmSyncResult.error}`}
                  </div>
                )}

                <div>
                  <button onClick={runKmSyncNow} disabled={runningKmSync}
                    style={{padding:"10px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:13,fontWeight:700,cursor:runningKmSync?"wait":"pointer"}}>
                    {runningKmSync?"Synchronisation…":"🔄 Synchroniser maintenant"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{background:T.card,border:`2px solid ${T.accent}`,borderRadius:15,padding:24}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <div style={{fontSize:15,fontWeight:700,color:T.text}}>📧 Rapport kilométrique par email</div>
              {kmRapportCfg?.enabled && <span style={{fontSize:10,fontWeight:800,color:"#fff",background:T.success,borderRadius:6,padding:"2px 8px"}}>ACTIVÉ</span>}
            </div>
            <div style={{fontSize:13,color:T.muted,marginBottom:18}}>
              Compte-rendu kilométrique hebdomadaire envoyé directement dans le corps de l'email (pas de PDF joint), aux destinataires et au jour/heure de ton choix.
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <span style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:6}}>Destinataires</span>
                <div style={{display:"flex",gap:8,marginBottom:8}}>
                  <input value={newKmDest} onChange={e=>setNewKmDest(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addKmDest()} placeholder="email@gs-group.fr"
                    style={{flex:1,maxWidth:280,background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none"}}/>
                  <button onClick={addKmDest} disabled={!newKmDest.trim()}
                    style={{padding:"9px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:13,fontWeight:700,cursor:"pointer"}}>
                    ➕ Ajouter
                  </button>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {kmRapportForm.destinataires.map(email=>(
                    <span key={email} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:600,background:T.accentDim,color:T.accent,padding:"4px 10px",borderRadius:20}}>
                      {email}
                      <button onClick={()=>removeKmDest(email)} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,lineHeight:1,padding:0}}>✕</button>
                    </span>
                  ))}
                </div>
              </div>

              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                <label style={{display:"flex",flexDirection:"column",gap:5}}>
                  <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Jour d'envoi</span>
                  <select value={kmRapportForm.jourSemaine} onChange={e=>setKmRapportForm(f=>({...f,jourSemaine:parseInt(e.target.value)}))}
                    style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",cursor:"pointer"}}>
                    {["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"].map((j,idx)=><option key={idx} value={idx}>{j}</option>)}
                  </select>
                </label>
                <label style={{display:"flex",flexDirection:"column",gap:5}}>
                  <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Heure</span>
                  <input type="time" step={3600} value={kmRapportForm.heure} onChange={e=>setKmRapportForm(f=>({...f,heure:e.target.value.slice(0,2)+":00"}))}
                    style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none"}}/>
                </label>
                <label style={{display:"flex",flexDirection:"column",gap:5}}>
                  <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Prix carburant (€/L)</span>
                  <input type="number" step="0.01" min="0" value={kmRapportForm.prixCarburant} onChange={e=>setKmRapportForm(f=>({...f,prixCarburant:parseFloat(e.target.value)||0}))}
                    style={{width:110,background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none"}}/>
                </label>
              </div>
              <div style={{fontSize:11,color:T.muted}}>
                Utilisé pour estimer le coût carburant hebdomadaire par véhicule (km parcourus × consommation moyenne renseignée sur la fiche véhicule × ce prix), affiché dans ce rapport et dans le Parc véhicules.
              </div>

              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none"}}>
                <input type="checkbox" checked={kmRapportForm.enabled} onChange={e=>setKmRapportForm(f=>({...f,enabled:e.target.checked}))}/>
                <span style={{fontSize:13,color:T.text}}>Activer l'envoi automatique hebdomadaire</span>
              </label>

              {kmRapportResult && (
                <div style={{background:T.bg,border:`1px solid ${kmRapportResult.ok?T.success:"#ef4444"}`,borderRadius:10,padding:12,fontSize:12.5,fontWeight:600,color:kmRapportResult.ok?T.success:"#ef4444"}}>
                  {kmRapportResult.ok ? `✅ Rapport envoyé à ${kmRapportResult.destinataires?.join(", ")}` : `❌ ${kmRapportResult.error}`}
                </div>
              )}

              <div style={{display:"flex",gap:10}}>
                <button onClick={sendKmRapportNow} disabled={sendingKmRapportNow||!kmRapportForm.destinataires.length}
                  style={{padding:"10px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:13,fontWeight:700,cursor:sendingKmRapportNow?"wait":"pointer"}}>
                  {sendingKmRapportNow?"Envoi…":"📤 Envoyer maintenant (test)"}
                </button>
                <button onClick={saveKmRapportConfig} disabled={savingKmRapport}
                  style={{padding:"10px 16px",borderRadius:9,border:"none",background:`linear-gradient(135deg,${T.accent},${T.purple})`,color:"#fff",fontSize:13,fontWeight:700,cursor:savingKmRapport?"wait":"pointer"}}>
                  {savingKmRapport?"Enregistrement…":"💾 Enregistrer"}
                </button>
              </div>
            </div>
          </div>

          <div style={{background:T.card,border:`2px solid ${T.accent}`,borderRadius:15,padding:24}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <div style={{fontSize:15,fontWeight:700,color:T.text}}>✉️ Rappel de saisie kilométrique (véhicules hors Océan)</div>
              {kmReminderCfg?.enabled && <span style={{fontSize:10,fontWeight:800,color:"#fff",background:T.success,borderRadius:6,padding:"2px 8px"}}>ACTIVÉ</span>}
            </div>
            <div style={{fontSize:13,color:T.muted,marginBottom:18}}>
              Email avec lien unique envoyé au conducteur/référent des véhicules sans boîtier Océan (fiche véhicule &gt; "Exclure du suivi Océan"), pour qu'il saisisse son kilométrage sans se connecter. Réglage du jour/heure d'envoi automatique + envoi manuel ci-dessous.
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
                <label style={{display:"flex",flexDirection:"column",gap:5}}>
                  <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Jour d'envoi</span>
                  <select value={kmReminderForm.jourSemaine} onChange={e=>setKmReminderForm(f=>({...f,jourSemaine:parseInt(e.target.value)}))}
                    style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",cursor:"pointer"}}>
                    {["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"].map((j,idx)=><option key={idx} value={idx}>{j}</option>)}
                  </select>
                </label>
                <label style={{display:"flex",flexDirection:"column",gap:5}}>
                  <span style={{fontSize:11,fontWeight:700,color:T.muted}}>Heure</span>
                  <input type="time" step={3600} value={kmReminderForm.heure} onChange={e=>setKmReminderForm(f=>({...f,heure:e.target.value.slice(0,2)+":00"}))}
                    style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none"}}/>
                </label>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none",paddingBottom:9}}>
                  <input type="checkbox" checked={kmReminderForm.enabled} onChange={e=>setKmReminderForm(f=>({...f,enabled:e.target.checked}))}/>
                  <span style={{fontSize:13,color:T.text}}>Activer l'envoi automatique</span>
                </label>
                <button onClick={saveKmReminderConfig} disabled={savingKmReminder}
                  style={{padding:"10px 16px",borderRadius:9,border:"none",background:`linear-gradient(135deg,${T.accent},${T.purple})`,color:"#fff",fontSize:13,fontWeight:700,cursor:savingKmReminder?"wait":"pointer"}}>
                  {savingKmReminder?"Enregistrement…":"💾 Enregistrer"}
                </button>
              </div>

              <div style={{borderTop:`1px solid ${T.border}`,paddingTop:14,marginTop:2}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <span style={{fontSize:12,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.5px"}}>Envoi manuel — {kmReminderVehicules.length} véhicule{kmReminderVehicules.length>1?"s":""} concerné{kmReminderVehicules.length>1?"s":""}</span>
                  <button onClick={sendKmReminderAll} disabled={sendingKmReminderAll||loadingKmReminderVeh||!kmReminderVehicules.length}
                    style={{padding:"8px 14px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:12,fontWeight:700,cursor:sendingKmReminderAll?"wait":"pointer"}}>
                    {sendingKmReminderAll?"Envoi…":"📤 Envoyer à tous les véhicules sans relevé cette semaine"}
                  </button>
                </div>

                {kmReminderResult && (
                  <div style={{background:T.bg,border:`1px solid ${kmReminderResult.ok?T.success:"#ef4444"}`,borderRadius:10,padding:12,fontSize:12.5,fontWeight:600,color:kmReminderResult.ok?T.success:"#ef4444",marginBottom:10}}>
                    {kmReminderResult.bulk
                      ? `✅ ${kmReminderResult.envoyes}/${kmReminderResult.total} email(s) envoyé(s)`
                      : (kmReminderResult.ok ? `✅ Rappel envoyé à ${kmReminderResult.destEmail} (${kmReminderResult.immatriculation})` : `❌ ${kmReminderResult.immatriculation||""} — ${kmReminderResult.error}`)}
                  </div>
                )}

                {loadingKmReminderVeh ? (
                  <div style={{fontSize:12,color:T.muted,fontStyle:"italic"}}>Chargement…</div>
                ) : kmReminderVehicules.length===0 ? (
                  <div style={{fontSize:12,color:T.muted,fontStyle:"italic"}}>Aucun véhicule exclu du suivi Océan pour le moment.</div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {kmReminderVehicules.map(v=>(
                      <div key={v.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:9,background:T.bg,border:`1px solid ${T.border}`}}>
                        <span style={{fontFamily:"monospace",fontWeight:700,fontSize:12,color:T.accent,width:90}}>{v.immatriculation}</span>
                        <span style={{fontSize:12,color:T.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.marque} {v.modele}</span>
                        <span style={{fontSize:11,color:T.muted}}>{v.email||v.technicien_email||"⚠️ aucun email"}</span>
                        <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20,background:v.releve_cette_semaine?`${T.success}18`:"#f59e0b18",color:v.releve_cette_semaine?T.success:"#f59e0b"}}>
                          {v.releve_cette_semaine?"✅ relevé fait":"⏳ en attente"}
                        </span>
                        <button onClick={()=>sendKmReminderOne(v)} disabled={sendingKmReminderId===v.id||(!v.email&&!v.technicien_email)}
                          style={{padding:"5px 10px",borderRadius:7,border:`1px solid ${T.border}`,background:"transparent",color:T.text,fontSize:11,fontWeight:700,cursor:sendingKmReminderId===v.id?"wait":"pointer"}}>
                          {sendingKmReminderId===v.id?"…":"📧 Envoyer"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{background:T.card,border:`2px solid ${T.accent}`,borderRadius:15,padding:24}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <div style={{fontSize:15,fontWeight:700,color:T.text}}>🔁 Synchronisation depuis crm.gs-group.fr</div>
              <span style={{fontSize:10,fontWeight:800,color:T.accent,background:T.accentDim,borderRadius:6,padding:"2px 8px"}}>RECOMMANDÉ</span>
            </div>
            <div style={{fontSize:13,color:T.muted,marginBottom:18}}>Endpoint utilisé par le bouton "Créer une intervention" du CRM (installation, SAV, SAV suite à un appel téléphonique). Rejouer la même requête avec le même <span style={{fontFamily:"monospace"}}>crm_intervention_id</span> met à jour la fiche existante au lieu d'en créer une seconde.</div>

            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <span style={{fontSize:11,fontWeight:800,color:"#fff",background:T.success,borderRadius:6,padding:"3px 8px"}}>POST</span>
                <span style={{fontFamily:"monospace",fontSize:12,color:T.text,background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 10px",flex:1,overflowX:"auto"}}>https://support.gs-group.fr/api/v1/interventions/sync</span>
              </div>
              <div style={{fontSize:12,color:T.muted}}>En-tête : <span style={{fontFamily:"monospace",color:T.text}}>X-API-KEY: &lt;votre clé&gt;</span> ou <span style={{fontFamily:"monospace",color:T.text}}>Authorization: Bearer &lt;votre clé&gt;</span></div>
            </div>

            <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>Corps de requête (JSON)</div>
            <pre style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:14,fontSize:12,color:T.text,overflowX:"auto",margin:0,fontFamily:"monospace",lineHeight:1.6}}>
{`{
  "crm_intervention_id": "INT-2026-XXXX",
  "type": "installation" | "sav" | "sav_telephone",
  "category": "caisse" | "alarme" | "video" | "autre",
  "client": {
    "id": "CLI-123",
    "name": "Nom du Client",
    "phone": "+33XXXXXXX",
    "email": "client@email.com"
  },
  "description": "Détails de l'intervention ou compte-rendu de l'appel",
  "priority": "normal" | "urgent",
  "created_at": "2026-07-29T16:00:00Z"
}`}
            </pre>
            <div style={{fontSize:12,color:T.muted,marginTop:10}}>Obligatoires : <span style={{color:T.text,fontWeight:700}}>crm_intervention_id</span>, <span style={{color:T.text,fontWeight:700}}>type</span>, <span style={{color:T.text,fontWeight:700}}>client.name</span>. La <span style={{fontFamily:"monospace"}}>category</span> choisit automatiquement le modèle de PV (vidéo → Pack Sentinelle, alarme → Pack Flash, caisse → Pack Cash Box).</div>

            <div style={{fontSize:11,fontWeight:700,color:T.muted,marginTop:16,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>Réponse</div>
            <pre style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:14,fontSize:12,color:T.text,overflowX:"auto",margin:0,fontFamily:"monospace",lineHeight:1.6}}>
{`{
  "ok": true,
  "support_intervention_id": "uuid-côté-support",
  "reference": "INT-XXXXXXXX",
  "crm_intervention_id": "INT-2026-XXXX",
  "statut": "planifiee"
}`}
            </pre>
            <div style={{fontSize:12,color:T.muted,marginTop:10}}>Conservez <span style={{fontFamily:"monospace"}}>support_intervention_id</span> côté CRM : il sert à consulter le statut ou récupérer les PV/SAV (voir plus bas).</div>
          </div>

          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:15,padding:24}}>
            <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:6}}>🔌 Format alternatif (autre intégration)</div>
            <div style={{fontSize:13,color:T.muted,marginBottom:18}}>Un format plus simple, pour un autre logiciel qui n'a pas besoin de la logique installation/SAV ci-dessus. Non utilisé par crm.gs-group.fr.</div>

            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <span style={{fontSize:11,fontWeight:800,color:"#fff",background:T.success,borderRadius:6,padding:"3px 8px"}}>POST</span>
                <span style={{fontFamily:"monospace",fontSize:12,color:T.text,background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 10px",flex:1,overflowX:"auto"}}>https://support.gs-group.fr/api/integrations/interventions</span>
              </div>
              <div style={{fontSize:12,color:T.muted}}>En-tête requis : <span style={{fontFamily:"monospace",color:T.text}}>x-api-key: &lt;votre clé&gt;</span></div>
            </div>

            <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>Exemple de corps de requête (JSON)</div>
            <pre style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:14,fontSize:12,color:T.text,overflowX:"auto",margin:0,fontFamily:"monospace",lineHeight:1.6}}>
{`{
  "titre": "Titre de l'intervention",
  "client_nom": "Nom du client",
  "client_external_id": "id-dans-l-autre-crm",
  "adresse": "12 rue Exemple",
  "ville": "Lyon",
  "code_postal": "69000",
  "date_intervention": "2026-08-01T09:00:00",
  "priorite": "low|medium|high|urgent",
  "type": "maintenance"
}`}
            </pre>
            <div style={{fontSize:12,color:T.muted,marginTop:10}}>Seuls <span style={{color:T.text,fontWeight:700}}>titre</span> et <span style={{color:T.text,fontWeight:700}}>client_nom</span> sont obligatoires. Passer <span style={{fontFamily:"monospace"}}>client_external_id</span> évite de créer un doublon de client si l'intervention est renvoyée plus tard.</div>
          </div>

          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:15,padding:24}}>
            <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:6}}>📄 Récupérer les documents (interventions, PV, SAV)</div>
            <div style={{fontSize:13,color:T.muted,marginBottom:18}}>Votre CRM vient chercher les données avec votre clé API — aucune configuration d'URL de notre côté n'est nécessaire. Idéal pour un poll périodique (ex : toutes les X minutes) qui récupère les interventions terminées et leurs documents.</div>

            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                  <span style={{fontSize:11,fontWeight:800,color:"#fff",background:T.accent,borderRadius:6,padding:"3px 8px"}}>GET</span>
                  <span style={{fontFamily:"monospace",fontSize:12,color:T.text,background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 10px",flex:1,overflowX:"auto"}}>/api/integrations/interventions?statut=completed&amp;since=2026-07-01T00:00:00Z</span>
                </div>
                <div style={{fontSize:12,color:T.muted}}>Liste des interventions (filtrable par statut et par date de mise à jour), avec indicateur <span style={{fontFamily:"monospace"}}>pv_valide</span> / <span style={{fontFamily:"monospace"}}>sav_valide</span>.</div>
              </div>

              <div>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                  <span style={{fontSize:11,fontWeight:800,color:"#fff",background:T.accent,borderRadius:6,padding:"3px 8px"}}>GET</span>
                  <span style={{fontFamily:"monospace",fontSize:12,color:T.text,background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 10px",flex:1,overflowX:"auto"}}>/api/integrations/interventions/:id</span>
                </div>
                <div style={{fontSize:12,color:T.muted}}>Détail complet d'une intervention, avec les objets <span style={{fontFamily:"monospace"}}>pv</span> et <span style={{fontFamily:"monospace"}}>sav</span> (données + <span style={{fontFamily:"monospace"}}>pdf_url</span>) s'ils existent.</div>
              </div>

              <div>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                  <span style={{fontSize:11,fontWeight:800,color:"#fff",background:T.accent,borderRadius:6,padding:"3px 8px"}}>GET</span>
                  <span style={{fontFamily:"monospace",fontSize:12,color:T.text,background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:"4px 10px",flex:1,overflowX:"auto"}}>/api/integrations/interventions/:id/pdf?type=pv|sav</span>
                </div>
                <div style={{fontSize:12,color:T.muted}}>Télécharge le PDF du document (même rendu que celui généré dans l'application).</div>
              </div>
            </div>
            <div style={{fontSize:12,color:T.muted,marginTop:14}}>Toutes ces routes utilisent le même en-tête <span style={{fontFamily:"monospace",color:T.text}}>x-api-key</span> que la création d'intervention.</div>
          </div>

          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:15,padding:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{fontSize:15,fontWeight:700,color:T.text}}>🔑 Clés API</div>
            </div>

            <div style={{display:"flex",gap:8,marginBottom:18}}>
              <input value={newKeyLabel} onChange={e=>setNewKeyLabel(e.target.value)} placeholder="Nom de l'intégration (ex : CRM Partenaire)"
                style={{flex:1,background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none"}}
                onKeyDown={e=>{if(e.key==="Enter") createApiKey();}}/>
              <button onClick={createApiKey} disabled={creatingKey||!newKeyLabel.trim()}
                style={{padding:"10px 16px",borderRadius:9,border:"none",background:!newKeyLabel.trim()?T.border:`linear-gradient(135deg,${T.accent},${T.purple})`,color:"#fff",fontSize:13,fontWeight:700,cursor:!newKeyLabel.trim()?"not-allowed":"pointer",whiteSpace:"nowrap"}}>
                {creatingKey?"Génération…":"+ Générer une clé"}
              </button>
            </div>

            {loadingKeys&&<div style={{fontSize:13,color:T.muted}}>Chargement…</div>}
            {!loadingKeys&&apiKeys.length===0&&<div style={{fontSize:13,color:T.muted}}>Aucune clé API pour le moment.</div>}

            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {apiKeys.map(k=>(
                <div key={k.id} style={{border:`1px solid ${T.border}`,borderRadius:11,padding:14,background:k.actif?T.bg:"transparent",opacity:k.actif?1:0.6}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,gap:10}}>
                    <div style={{fontSize:13,fontWeight:700,color:T.text}}>{k.label}</div>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      {!k.actif&&<span style={{fontSize:10,fontWeight:800,color:"#fff",background:"#ef4444",borderRadius:6,padding:"2px 7px"}}>RÉVOQUÉE</span>}
                      {k.actif&&(
                        <button onClick={()=>revokeApiKey(k.id)} style={{fontSize:11,fontWeight:700,color:"#ef4444",background:"#ef444422",border:"none",borderRadius:7,padding:"4px 9px",cursor:"pointer"}}>Révoquer</button>
                      )}
                      <button onClick={()=>deleteApiKey(k.id)} style={{fontSize:11,fontWeight:700,color:T.muted,background:T.card,border:`1px solid ${T.border}`,borderRadius:7,padding:"4px 9px",cursor:"pointer"}}>Supprimer</button>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <span style={{fontFamily:"monospace",fontSize:12,color:T.text,background:T.card,border:`1px solid ${T.border}`,borderRadius:6,padding:"5px 10px",flex:1,overflowX:"auto"}}>
                      {revealedKey===k.id ? k.api_key : k.api_key.slice(0,8)+"••••••••••••••••••••••••"}
                    </span>
                    <button onClick={()=>setRevealedKey(revealedKey===k.id?null:k.id)} title="Afficher / masquer"
                      style={{fontSize:13,background:"transparent",border:"none",cursor:"pointer",color:T.muted}}>{revealedKey===k.id?"🙈":"👁️"}</button>
                    <button onClick={()=>copyToClipboard(k.api_key,k.id)} title="Copier"
                      style={{fontSize:11,fontWeight:700,color:copiedKey===k.id?T.success:T.accent,background:T.accentDim,border:"none",borderRadius:7,padding:"5px 10px",cursor:"pointer",whiteSpace:"nowrap"}}>
                      {copiedKey===k.id?"Copié ✓":"Copier"}
                    </button>
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginTop:8}}>
                    Créée le {new Date(k.created_at).toLocaleDateString('fr-FR')}
                    {k.last_used_at?` · dernière utilisation le ${new Date(k.last_used_at).toLocaleDateString('fr-FR')}`:" · jamais utilisée"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ SAUVEGARDE ═══ */}
      {isAdmin&&tab==="sauvegarde"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:15,padding:24}}>
            <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:6}}>💾 Sauvegarde complète</div>
            <div style={{fontSize:13,color:T.muted,marginBottom:20}}>Génère un fichier ZIP contenant l'intégralité de la base de données (interventions, clients, techniciens, stock, photos, SAV…).</div>

            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10,marginBottom:22}}>
              {[
                {ic:"🏢",lbl:"Clients & contacts"},
                {ic:"🔧",lbl:"Interventions & SAV"},
                {ic:"👥",lbl:"Utilisateurs & rôles"},
                {ic:"📦",lbl:"Stock & matériel"},
                {ic:"📷",lbl:"Photos & documents"},
                {ic:"🚗",lbl:"Flotte & véhicules"},
                {ic:"🖥️",lbl:"Parc informatique"},
                {ic:"📊",lbl:"Statistiques & heures"},
              ].map(item=>(
                <div key={item.lbl} style={{display:"flex",alignItems:"center",gap:9,padding:"10px 13px",background:T.bg,border:`1px solid ${T.border}`,borderRadius:10}}>
                  <span style={{fontSize:18}}>{item.ic}</span>
                  <span style={{fontSize:12,fontWeight:600,color:T.text}}>{item.lbl}</span>
                </div>
              ))}
            </div>

            <button
              onClick={downloadBackup}
              disabled={backupLoading}
              style={{width:"100%",padding:"15px",borderRadius:12,background:backupLoading?"#6b7280":`linear-gradient(135deg,${T.accent},${T.purple})`,border:"none",color:"#fff",fontSize:15,fontWeight:800,cursor:backupLoading?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,transition:"all 0.2s"}}>
              {backupLoading
                ? <><span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⏳</span> Génération en cours…</>
                : <>💾 Télécharger la sauvegarde ZIP</>}
            </button>
            {backupLoading&&<div style={{fontSize:12,color:T.muted,textAlign:"center",marginTop:8}}>La génération peut prendre quelques secondes selon la taille de la base.</div>}
          </div>

          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:15,padding:18}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:8}}>ℹ️ Contenu du fichier ZIP</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <div style={{display:"flex",gap:10,alignItems:"center",fontSize:12,color:T.muted}}>
                <span style={{fontFamily:"monospace",background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:"2px 8px",color:T.text,fontSize:11}}>database-AAAA-MM-JJ.sql</span>
                <span>Dump PostgreSQL complet (restaurable avec psql)</span>
              </div>
              <div style={{display:"flex",gap:10,alignItems:"center",fontSize:12,color:T.muted}}>
                <span style={{fontFamily:"monospace",background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:"2px 8px",color:T.text,fontSize:11}}>backup_info.json</span>
                <span>Métadonnées de la sauvegarde (date, auteur)</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
